// ============================================================================
// API CLIENT FOR ISPOT BACKEND
// ============================================================================

import type {
  JourneyFramework,
  ConfigurableItemsResponse,
  UpdatePhaseConfigRequest,
  DocTemplateResponse,
  DocTemplatesListResponse,
  DocTemplateGetResponse,
  CreateDocTemplateRequest,
  UpdateDocTemplateConfigRequest,
  DocConfigurableItemsResponse,
  SectionTemplate,
  SectionTemplatesListResponse,
  CreateSectionTemplateRequest,
} from '@ispot/shared'

// Always use relative URLs - proxied through Vite (dev) or Caddy (prod)
export const API_URL = import.meta.env.VITE_API_URL ?? ''
const TOKEN_KEY = 'ispot-session-token'

// Token management for cross-origin auth
export function getSessionToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setSessionToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearSessionToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export interface ApiError {
  error: string
  message: string
}

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string
  isSuperAdmin?: boolean
}

export interface Account {
  id: string
  name: string
  slug: string
  role: 'owner' | 'admin' | 'member'
}

export interface Project {
  id: string
  name: string
  slug: string
  description?: string
  graphUri: string
  visualAnalysisEnabled?: boolean
  role: string
  createdAt: string
}

export interface ProjectMember {
  user: {
    id: string
    email: string
    name: string
    avatarUrl?: string
  }
  role: string
  joinedAt: string
}

export interface RoleDefinition {
  key: string
  name: string
  description: string
  permissions: string[]
}

// Fetch wrapper with token auth and error handling
async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getSessionToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: 'include',
    headers,
  })

  const data = await response.json()

  if (!response.ok) {
    const error = data as ApiError
    // On 401, clear session and persisted auth state to stop query loops
    if (response.status === 401) {
      clearSessionToken()
      // Clear persisted Zustand stores to prevent stale auth state
      localStorage.removeItem('ispot-auth-v3')
      localStorage.removeItem('ispot-project-v3')
    }
    throw new Error(error.error || error.message || `API error: ${response.status}`)
  }

  return data as T
}

// ============================================================================
// GENERIC API CLIENT
// ============================================================================

export const apiClient = {
  async get<T = unknown>(path: string): Promise<{ data: T }> {
    const data = await apiFetch<T>(path)
    return { data }
  },

  async post<T = unknown>(path: string, body?: unknown): Promise<{ data: T }> {
    const data = await apiFetch<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    })
    return { data }
  },

  async put<T = unknown>(path: string, body?: unknown): Promise<{ data: T }> {
    const data = await apiFetch<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    })
    return { data }
  },

  async patch<T = unknown>(path: string, body?: unknown): Promise<{ data: T }> {
    const data = await apiFetch<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    })
    return { data }
  },

  async delete<T = unknown>(path: string): Promise<{ data: T }> {
    const data = await apiFetch<T>(path, { method: 'DELETE' })
    return { data }
  },
}

// ============================================================================
// AUTH API
// ============================================================================

export const authApi = {
  async register(email: string, password: string, name: string, companyName: string): Promise<{ user: User; account: Account }> {
    const result = await apiFetch<{ user: User; account: Account; sessionId: string }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, companyName }),
    })
    // Store session token for cross-origin auth
    setSessionToken(result.sessionId)
    return { user: result.user, account: { ...result.account, role: 'owner' } }
  },

  async login(email: string, password: string): Promise<User> {
    const result = await apiFetch<{ user: User; sessionId: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    // Store session token for cross-origin auth
    setSessionToken(result.sessionId)
    return result.user
  },

  async logout(): Promise<void> {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' })
    } finally {
      clearSessionToken()
    }
  },

  async me(): Promise<{ user: User; accounts: Account[] } | null> {
    try {
      const result = await apiFetch<{ user: User; accounts: Account[] }>('/api/auth/me')
      return result
    } catch {
      clearSessionToken()
      return null
    }
  },
}

// ============================================================================
// PROJECTS API
// ============================================================================

export const projectsApi = {
  async list(accountId: string): Promise<Project[]> {
    const result = await apiFetch<{ projects: Project[] }>(`/api/accounts/${accountId}/projects`)
    return result.projects
  },

  async create(accountId: string, name: string, slug: string, description?: string): Promise<Project> {
    const result = await apiFetch<{ project: Project }>(`/api/accounts/${accountId}/projects`, {
      method: 'POST',
      body: JSON.stringify({ name, slug, description }),
    })
    return result.project
  },

  async get(accountId: string, projectId: string): Promise<Project> {
    const result = await apiFetch<{ project: Project }>(`/api/accounts/${accountId}/projects/${projectId}`)
    return result.project
  },

  async update(accountId: string, projectId: string, updates: { name?: string; description?: string; visualAnalysisEnabled?: boolean }): Promise<Project> {
    const result = await apiFetch<{ project: Project }>(`/api/accounts/${accountId}/projects/${projectId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
    return result.project
  },

  async delete(accountId: string, projectId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}`, { method: 'DELETE' })
  },

  async getMembers(accountId: string, projectId: string): Promise<ProjectMember[]> {
    const result = await apiFetch<{ members: ProjectMember[] }>(`/api/accounts/${accountId}/projects/${projectId}/members`)
    return result.members
  },

  async addMember(accountId: string, projectId: string, email: string, role: string): Promise<ProjectMember> {
    const result = await apiFetch<{ member: ProjectMember }>(`/api/accounts/${accountId}/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    })
    return result.member
  },

  async updateMember(accountId: string, projectId: string, userId: string, role: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    })
  },

  async removeMember(accountId: string, projectId: string, userId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/members/${userId}`, { method: 'DELETE' })
  },

  async getRoles(accountId: string, projectId: string): Promise<RoleDefinition[]> {
    const result = await apiFetch<{ roles: RoleDefinition[] }>(`/api/accounts/${accountId}/projects/${projectId}/roles`)
    return result.roles
  },
}

// ============================================================================
// CRM TYPES
// ============================================================================

export interface Person {
  id: string
  projectId: string
  firstName: string
  lastName: string
  email?: string | null
  phone?: string | null
  jobTitle?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
}

export interface Customer {
  id: string
  projectId: string
  customerName: string
  customerType?: 'prospect' | 'active' | 'churned' | 'partner' | null
  billingAddress?: string | null
  city?: string | null
  country?: string | null
  industry?: string | null
  website?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
}

export interface CustomerContact {
  person: Person
  role?: 'primary' | 'billing' | 'technical' | 'decision_maker' | null
  isPrimary: boolean
}

// ============================================================================
// CRM API - PERSONS (CONTACTS)
// ============================================================================

export const personsApi = {
  async list(accountId: string, projectId: string): Promise<Person[]> {
    const result = await apiFetch<{ persons: Person[] }>(`/api/accounts/${accountId}/projects/${projectId}/persons`)
    return result.persons
  },

  async get(accountId: string, projectId: string, personId: string): Promise<Person> {
    const result = await apiFetch<{ person: Person }>(`/api/accounts/${accountId}/projects/${projectId}/persons/${personId}`)
    return result.person
  },

  async create(accountId: string, projectId: string, data: {
    firstName: string
    lastName: string
    email?: string | null
    phone?: string | null
    jobTitle?: string | null
    notes?: string | null
  }): Promise<Person> {
    const result = await apiFetch<{ person: Person }>(`/api/accounts/${accountId}/projects/${projectId}/persons`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result.person
  },

  async update(accountId: string, projectId: string, personId: string, data: {
    firstName?: string
    lastName?: string
    email?: string | null
    phone?: string | null
    jobTitle?: string | null
    notes?: string | null
  }): Promise<Person> {
    const result = await apiFetch<{ person: Person }>(`/api/accounts/${accountId}/projects/${projectId}/persons/${personId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return result.person
  },

  async delete(accountId: string, projectId: string, personId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/persons/${personId}`, { method: 'DELETE' })
  },
}

// ============================================================================
// CRM API - CUSTOMERS
// ============================================================================

export const customersApi = {
  async list(accountId: string, projectId: string): Promise<Customer[]> {
    const result = await apiFetch<{ customers: Customer[] }>(`/api/accounts/${accountId}/projects/${projectId}/customers`)
    return result.customers
  },

  async get(accountId: string, projectId: string, customerId: string): Promise<{ customer: Customer; contacts: CustomerContact[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}`)
  },

  async create(accountId: string, projectId: string, data: {
    customerName: string
    customerType?: 'prospect' | 'active' | 'churned' | 'partner' | null
    billingAddress?: string | null
    city?: string | null
    country?: string | null
    industry?: string | null
    website?: string | null
    notes?: string | null
  }): Promise<Customer> {
    const result = await apiFetch<{ customer: Customer }>(`/api/accounts/${accountId}/projects/${projectId}/customers`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result.customer
  },

  async update(accountId: string, projectId: string, customerId: string, data: {
    customerName?: string
    customerType?: 'prospect' | 'active' | 'churned' | 'partner' | null
    billingAddress?: string | null
    city?: string | null
    country?: string | null
    industry?: string | null
    website?: string | null
    notes?: string | null
  }): Promise<Customer> {
    const result = await apiFetch<{ customer: Customer }>(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
    return result.customer
  },

  async delete(accountId: string, projectId: string, customerId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}`, { method: 'DELETE' })
  },

  async addContact(accountId: string, projectId: string, customerId: string, data: {
    personId: string
    role?: 'primary' | 'billing' | 'technical' | 'decision_maker' | null
    isPrimary?: boolean
  }): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}/contacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async removeContact(accountId: string, projectId: string, customerId: string, personId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/customers/${customerId}/contacts/${personId}`, { method: 'DELETE' })
  },
}

// ============================================================================
// SOURCES API (Generic Document Analyzer)
// ============================================================================

export type ContentType =
  | 'user_interview' | 'buyer_interview' | 'stakeholder_meeting'
  | 'team_meeting' | 'vision_document' | 'requirements_spec'
  | 'presentation' | 'research_notes' | 'competitor_analysis'
  | 'support_ticket' | 'survey_response' | 'custom'

export type SourceStatus =
  | 'uploading'
  | 'parsing'
  | 'analyzing'
  | 'detecting-entities'
  | 'ready-for-review'
  | 'completed'
  | 'error'
  // Legacy statuses
  | 'pending'
  | 'analyzed'

export type FacetStatus = 'pending' | 'accepted' | 'rejected' | 'mapped'

export interface Source {
  id: string
  projectId: string
  accountId: string
  createdById: string
  title: string
  contentType: ContentType
  rawContent: string
  markdownContent: string
  fileType?: string | null
  fileName?: string | null
  status: SourceStatus
  analyzedAt?: string | null
  analysisError?: string | null
  metadata?: Record<string, unknown>
  tags?: string[]
  createdAt: string
  updatedAt: string
}

export interface SourceWithStats extends Source {
  facetStats: {
    total: number
    pending: number
    accepted: number
    rejected: number
    mapped: number
  }
}

export interface DetectedFacet {
  id: string
  sourceId: string
  projectId: string
  facetTypeId: string
  title?: string | null
  content: string
  evidence: string | null
  confidence: number

  // Discriminator: 'text' or 'visual'
  sourceType?: 'text' | 'visual'

  // Text-specific position fields
  positionStart?: number | null
  positionEnd?: number | null

  // Visual-specific fields
  imageId?: string | null
  pageNumber?: number | null
  bboxX?: number | null
  bboxY?: number | null
  bboxWidth?: number | null
  bboxHeight?: number | null

  // Problem statement (Three C's)
  problemStatement?: {
    context: string
    cause: string
    consequence: string
  } | null

  suggestedEntityType?: string | null
  suggestedReason?: string | null
  status: FacetStatus
  mappedEntityType?: string | null
  mappedEntityUri?: string | null
  decidedAt?: string | null
  decidedById?: string | null
  graphUri?: string | null
  createdAt: string
}

export interface SourceWithFacets extends Source {
  facets: DetectedFacet[]
  facetStats: {
    total: number
    pending: number
    accepted: number
    rejected: number
    mapped: number
  }
}

// ============================================================================
// VISUAL ANALYSIS TYPES
// ============================================================================

export type ImageType =
  | 'logo' | 'business_canvas' | 'whiteboard' | 'architecture_diagram'
  | 'process_diagram' | 'ui_screenshot' | 'wireframe' | 'chart'
  | 'org_chart' | 'journey_map' | 'swot' | 'mindmap'
  | 'timeline' | 'table' | 'photo' | 'unknown'

export interface SourceImage {
  id: string
  sourceId: string
  projectId: string
  imageData?: string | null  // Base64 encoded
  thumbnailData?: string | null
  mimeType: string
  pageNumber: number
  bboxX?: number | null
  bboxY?: number | null
  bboxWidth?: number | null
  bboxHeight?: number | null
  imageType: ImageType
  classificationConfidence?: number | null
  analyzed: boolean
  analysisError?: string | null
  createdAt: string
}

export interface VisualFacet {
  id: string
  sourceId: string
  projectId: string
  imageId: string
  documentEntityId?: string | null
  facetType: string
  title: string
  content?: string | null
  evidence?: string | null
  confidence: number
  bboxX?: number | null
  bboxY?: number | null
  bboxWidth?: number | null
  bboxHeight?: number | null
  suggestedEntityType?: string | null
  suggestedReason?: string | null
  status: FacetStatus
  mappedEntityType?: string | null
  mappedEntityUri?: string | null
  decidedAt?: string | null
  decidedById?: string | null
  createdAt: string
}

// ============================================================================
// FACET ENTITY GROUPS (Many-to-One Mapping)
// ============================================================================

export type GroupStatus = 'draft' | 'confirmed' | 'created'
export type FacetRole = 'primary' | 'evidence' | 'variant'

export interface FacetEntityGroup {
  id: string
  projectId: string
  sourceId: string
  createdById?: string | null
  entityLabel: string
  entityType: string
  entityUri?: string | null
  entityDescription?: string | null
  aiSuggested: boolean
  aiConfidence?: number | null
  aiReason?: string | null
  status: GroupStatus
  createdAt: string
  updatedAt: string
}

export interface FacetGroupMember {
  groupId: string
  facetId: string
  role: FacetRole
  addedAt: string
  facet?: DetectedFacet
}

export interface FacetEntityGroupWithMembers extends FacetEntityGroup {
  members: FacetGroupMember[]
}

export interface EntityGroupWithFacets extends FacetEntityGroup {
  facets: Array<DetectedFacet & { role: FacetRole }>
}

export interface FacetConfiguration {
  id: string
  projectId: string
  facetTypeId: string
  isEnabled: boolean
  customEntityType?: string | null
  updatedAt: string
}

export const sourcesApi = {
  async list(accountId: string, projectId: string): Promise<SourceWithStats[]> {
    const result = await apiFetch<{ sources: SourceWithStats[] }>(`/api/accounts/${accountId}/projects/${projectId}/sources`)
    return result.sources
  },

  async get(accountId: string, projectId: string, sourceId: string): Promise<SourceWithFacets> {
    const result = await apiFetch<{ source: SourceWithFacets }>(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}`)
    return result.source
  },

  async create(accountId: string, projectId: string, data: {
    title: string
    contentType: ContentType
    rawContent: string
    markdownContent?: string
    fileName?: string
    fileType?: 'pdf' | 'md' | 'txt'
    fileData?: string // Base64 encoded file binary
    tags?: string[]
    metadata?: Record<string, unknown>
  }): Promise<Source> {
    const result = await apiFetch<{ source: Source }>(`/api/accounts/${accountId}/projects/${projectId}/sources`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
    return result.source
  },

  async update(accountId: string, projectId: string, sourceId: string, data: {
    title?: string
    contentType?: ContentType
    tags?: string[]
    metadata?: Record<string, unknown>
  }): Promise<Source> {
    const result = await apiFetch<{ source: Source }>(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    return result.source
  },

  async delete(accountId: string, projectId: string, sourceId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}`, { method: 'DELETE' })
  },

  async analyze(accountId: string, projectId: string, sourceId: string, enabledFacetIds?: string[]): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/analyze`, {
      method: 'POST',
      body: JSON.stringify({ enabledFacetIds }),
    })
  },

  async saveFacets(
    accountId: string,
    projectId: string,
    sourceId: string,
    facets: Array<{
      facetTypeId: string
      content: string
      evidence: string
      confidence: number
      positionStart?: number
      positionEnd?: number
      suggestedEntityType?: string
      suggestedReason?: string
    }>,
    entities?: Array<{
      id: string
      type: string
      name: string
      description: string
      evidence: string
      confidence: number
    }>,
    relationships?: Array<{
      from: string
      to: string
      type: string
      evidence: string
      confidence: number
    }>,
    visualFacets?: Array<{
      facetTypeId: string
      title: string
      content: string
      evidence?: string
      confidence: number
      pageNumber?: number
      bboxX?: number
      bboxY?: number
      bboxWidth?: number
      bboxHeight?: number
      suggestedEntityType?: string
      suggestedReason?: string
    }>
  ): Promise<{
    facets: DetectedFacet[]
    facetCount: number
    textFacetCount?: number
    visualFacetCount?: number
    entitiesCreated: number
    relationshipsCreated: number
  }> {
    const result = await apiFetch<{
      facets: DetectedFacet[]
      facetCount: number
      textFacetCount?: number
      visualFacetCount?: number
      entitiesCreated: number
      relationshipsCreated: number
    }>(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets`, {
      method: 'POST',
      body: JSON.stringify({ facets, entities, relationships, visualFacets }),
    })
    return result
  },

  async updateFacet(accountId: string, projectId: string, sourceId: string, facetId: string, data: {
    action: 'accept' | 'reject'
    entityType?: string
  }): Promise<DetectedFacet> {
    const result = await apiFetch<{ facet: DetectedFacet }>(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/${facetId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
    return result.facet
  },

  async bulkUpdateFacets(accountId: string, projectId: string, sourceId: string, facetIds: string[], action: 'accept' | 'reject'): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/bulk`, {
      method: 'POST',
      body: JSON.stringify({ facetIds, action }),
    })
  },

  async getFacetConfig(accountId: string, projectId: string): Promise<FacetConfiguration[]> {
    const result = await apiFetch<{ configurations: FacetConfiguration[] }>(`/api/accounts/${accountId}/projects/${projectId}/sources/facet-config`)
    return result.configurations
  },

  async updateFacetConfig(accountId: string, projectId: string, configs: Array<{
    facetTypeId: string
    isEnabled: boolean
    customEntityType?: string
  }>): Promise<FacetConfiguration[]> {
    const result = await apiFetch<{ configurations: FacetConfiguration[] }>(`/api/accounts/${accountId}/projects/${projectId}/sources/facet-config`, {
      method: 'PUT',
      body: JSON.stringify({ configurations: configs }),
    })
    return result.configurations
  },

  // Entity creation from facets
  async createEntityFromFacet(accountId: string, projectId: string, sourceId: string, facetId: string, data?: {
    entityType?: string
    entityLabel?: string
    entityDescription?: string
  }): Promise<{
    success: boolean
    facet: DetectedFacet
    entity: { uri: string; type: string; label: string }
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/${facetId}/entity`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    })
  },

  async bulkCreateEntities(accountId: string, projectId: string, sourceId: string, facetIds: string[]): Promise<{
    success: boolean
    created: number
    failed: number
    results: Array<{
      facetId: string
      success: boolean
      entityUri?: string
      entityType?: string
      error?: string
    }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/entities`, {
      method: 'POST',
      body: JSON.stringify({ facetIds }),
    })
  },

  // ============================================================================
  // FACET ENTITY GROUPS (Many-to-One Mapping)
  // ============================================================================

  async listGroups(accountId: string, projectId: string, sourceId: string): Promise<FacetEntityGroupWithMembers[]> {
    const result = await apiFetch<{ groups: FacetEntityGroupWithMembers[] }>(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups`
    )
    return result.groups
  },

  async createGroup(accountId: string, projectId: string, sourceId: string, data: {
    entityLabel: string
    entityType: string
    entityDescription?: string
    facetIds: string[]
    primaryFacetId?: string
  }): Promise<FacetEntityGroupWithMembers> {
    const result = await apiFetch<{ group: FacetEntityGroupWithMembers }>(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.group
  },

  async updateGroup(accountId: string, projectId: string, sourceId: string, groupId: string, data: {
    entityLabel?: string
    entityType?: string
    entityDescription?: string
    status?: GroupStatus
    addFacetIds?: string[]
    removeFacetIds?: string[]
    primaryFacetId?: string
  }): Promise<FacetEntityGroupWithMembers> {
    const result = await apiFetch<{ group: FacetEntityGroupWithMembers }>(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups/${groupId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
    return result.group
  },

  async deleteGroup(accountId: string, projectId: string, sourceId: string, groupId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups/${groupId}`,
      { method: 'DELETE' }
    )
  },

  async createEntityFromGroup(accountId: string, projectId: string, sourceId: string, groupId: string): Promise<{
    success: boolean
    group: FacetEntityGroupWithMembers
    entity: { uri: string; type: string; label: string }
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/groups/${groupId}/entity`,
      { method: 'POST' }
    )
  },

  async bulkCreateFromGroupsAndFacets(accountId: string, projectId: string, sourceId: string, data: {
    groupIds: string[]
    facetIds: string[]
  }): Promise<{
    success: boolean
    groupResults: Array<{
      groupId: string
      success: boolean
      entityUri?: string
      error?: string
    }>
    facetResults: Array<{
      facetId: string
      success: boolean
      entityUri?: string
      error?: string
    }>
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/bulk-create`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // ============================================================================
  // FACET-ENTITY GRAPH LINKING (Many-to-One)
  // ============================================================================

  /** Link a facet to an existing entity in the graph */
  async linkFacetToEntity(accountId: string, projectId: string, sourceId: string, facetId: string, entityUri: string): Promise<{
    success: boolean
    facet: DetectedFacet
    link: { facetUri: string; entityUri: string }
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/${facetId}/link`,
      {
        method: 'POST',
        body: JSON.stringify({ entityUri }),
      }
    )
  },

  /** Bulk link multiple facets to one entity */
  async bulkLinkFacets(accountId: string, projectId: string, sourceId: string, facetIds: string[], entityUri: string): Promise<{
    success: boolean
    linked: number
    failed: number
    entityUri: string
    results: Array<{
      facetId: string
      success: boolean
      facetUri?: string
      error?: string
    }>
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/bulk-link`,
      {
        method: 'POST',
        body: JSON.stringify({ facetIds, entityUri }),
      }
    )
  },

  /** Unlink a facet from its entity */
  async unlinkFacet(accountId: string, projectId: string, sourceId: string, facetId: string): Promise<{
    success: boolean
    facet: DetectedFacet
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/facets/${facetId}/link`,
      { method: 'DELETE' }
    )
  },

  /** Search entities with facet counts */
  async searchEntitiesForLinking(accountId: string, projectId: string, query: string, entityType?: string, limit = 20): Promise<{
    entities: Array<{
      uri: string
      label: string
      type: string
      facetCount: number
    }>
  }> {
    const params = new URLSearchParams({ q: query, limit: String(limit) })
    if (entityType) params.set('entityType', entityType)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/graph/entities/search?${params}`
    )
  },

  /** Get all facets linked to an entity */
  async getFacetsForEntity(accountId: string, projectId: string, entityUri: string): Promise<{
    facets: Array<{
      uri: string
      facetType: string
      content: string
      evidence: string
      confidence: number
      sourceUri?: string
      sourceTitle?: string
      status?: string
      createdAt?: string
    }>
    entityUri: string
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/graph/entities/${encodeURIComponent(entityUri)}/facets`
    )
  },

  /** Backfill: Sync existing mapped facets to graph */
  async backfillFacetsToGraph(accountId: string, projectId: string): Promise<{
    success: boolean
    message: string
    synced: number
    failed?: number
    errors?: string[]
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/backfill/facets-to-graph`,
      { method: 'POST' }
    )
  },

  /** Backfill: Auto-accept pending facets */
  async backfillAutoAccept(accountId: string, projectId: string): Promise<{
    success: boolean
    message: string
    updated: number
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/backfill/auto-accept`,
      { method: 'POST' }
    )
  },

  // ============================================================================
  // VISUAL ANALYSIS
  // ============================================================================

  /** Get extracted images for a source */
  async getImages(accountId: string, projectId: string, sourceId: string): Promise<{
    images: Array<{
      id: string
      sourceId: string
      mimeType: string
      pageNumber: number
      bboxX?: number | null
      bboxY?: number | null
      bboxWidth?: number | null
      bboxHeight?: number | null
      imageType: ImageType
      classificationConfidence?: number | null
      analyzed: boolean
      createdAt: string
    }>
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/images`
    )
  },

  /** Get single image with data */
  async getImage(accountId: string, projectId: string, sourceId: string, imageId: string): Promise<{
    image: SourceImage
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/images/${imageId}`
    )
  },

  /** Save extracted images */
  async saveImages(accountId: string, projectId: string, sourceId: string, images: Array<{
    id: string
    data: string
    mimeType?: string
    pageNumber: number
    width?: number
    height?: number
    bboxX?: number
    bboxY?: number
    bboxWidth?: number
    bboxHeight?: number
  }>): Promise<{
    success: boolean
    images: Array<{ id: string; originalId: string; pageNumber: number }>
    count: number
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/images`,
      {
        method: 'POST',
        body: JSON.stringify({ images }),
      }
    )
  },

  /** Get visual facets for a source */
  async getVisualFacets(accountId: string, projectId: string, sourceId: string): Promise<{
    facets: VisualFacet[]
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/visual-facets`
    )
  },

  /** Save visual facets */
  async saveVisualFacets(
    accountId: string,
    projectId: string,
    sourceId: string,
    facets: Array<{
      imageId: string
      facetType: string
      title: string
      content?: string
      evidence?: string
      confidence: number
      bboxX?: number
      bboxY?: number
      bboxWidth?: number
      bboxHeight?: number
      suggestedEntityType?: string
      suggestedReason?: string
    }>,
    classifications?: Array<{
      imageId: string
      category: string
      confidence: number
    }>
  ): Promise<{
    success: boolean
    facets: VisualFacet[]
    count: number
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/visual-facets`,
      {
        method: 'POST',
        body: JSON.stringify({ facets, classifications }),
      }
    )
  },

  /** Get PDF file data for a source */
  async getFileData(accountId: string, projectId: string, sourceId: string): Promise<{
    fileData: string  // Base64 encoded
    fileType: string | null
    fileName: string | null
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/file`
    )
  },

  // ============================================================================
  // DOCUMENT ANALYSIS WORKFLOW
  // ============================================================================

  async getAnalysisStatus(accountId: string, projectId: string, sourceId: string): Promise<{
    status: SourceStatus
    stage: string
    progress: number
    message: string
    error?: string
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/analysis-status`
    )
  },

  async getEntityGroups(accountId: string, projectId: string, sourceId: string): Promise<EntityGroupWithFacets[]> {
    const result = await apiFetch<{ entityGroups: EntityGroupWithFacets[] }>(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/entity-groups`
    )
    return result.entityGroups
  },

  async approveEntityGroup(accountId: string, projectId: string, sourceId: string, groupId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/entity-groups/${groupId}/approve`,
      { method: 'POST' }
    )
  },

  async rejectEntityGroup(accountId: string, projectId: string, sourceId: string, groupId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sources/${sourceId}/entity-groups/${groupId}/reject`,
      { method: 'POST' }
    )
  },
}

// ============================================================================
// WEB SOURCES API - Website scraping and entity extraction
// ============================================================================

export interface CrawlConfig {
  maxDepth: number
  maxPages: number
  includePaths?: string[]
  excludePaths?: string[]
  delayMs?: number
}

export interface CrawlProgress {
  pagesFound: number
  pagesCrawled: number
  currentUrl?: string
  errors: Array<{ url: string; error: string }>
}

export interface WebSource {
  id: string
  sourceId: string | null
  projectId: string
  accountId: string
  url: string
  domain: string | null
  title: string | null
  description: string | null
  crawlConfig: CrawlConfig | null
  crawlStatus: 'pending' | 'fetching' | 'extracting' | 'completed' | 'error'
  crawlProgress: CrawlProgress | null
  errorMessage: string | null
  scrapedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface WebSourceStatus {
  id: string
  crawlStatus: 'pending' | 'fetching' | 'extracting' | 'completed' | 'error'
  crawlProgress: CrawlProgress | null
  errorMessage: string | null
  title: string | null
  sourceId: string | null
}

export interface ScrapeResult {
  success: boolean
  webSourceId: string
  sourceId?: string
  title?: string
  pageCount: number
  entityCount?: number
  error?: string
}

export const webSourcesApi = {
  async scrape(
    accountId: string,
    projectId: string,
    data: { url: string; crawlConfig?: CrawlConfig }
  ): Promise<ScrapeResult> {
    const result = await apiFetch<{ data: ScrapeResult }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/scrape`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.data
  },

  async list(accountId: string, projectId: string): Promise<WebSource[]> {
    const result = await apiFetch<{ data: WebSource[] }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources`
    )
    return result.data
  },

  async get(accountId: string, projectId: string, webSourceId: string): Promise<WebSource> {
    const result = await apiFetch<{ data: WebSource }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/${webSourceId}`
    )
    return result.data
  },

  async getStatus(accountId: string, projectId: string, webSourceId: string): Promise<WebSourceStatus> {
    const result = await apiFetch<{ data: WebSourceStatus }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/${webSourceId}/status`
    )
    return result.data
  },

  async getPages(
    accountId: string,
    projectId: string,
    webSourceId: string
  ): Promise<Array<{
    id: string
    url: string
    title: string | null
    depth: number
    status: string
    errorMessage: string | null
    fetchedAt: string | null
  }>> {
    const result = await apiFetch<{ data: Array<{
      id: string
      url: string
      title: string | null
      depth: number
      status: string
      errorMessage: string | null
      fetchedAt: string | null
    }> }>(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/${webSourceId}/pages`
    )
    return result.data
  },

  async delete(accountId: string, projectId: string, webSourceId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/web-sources/${webSourceId}`,
      { method: 'DELETE' }
    )
  },
}

// ============================================================================
// FACET CATEGORY CONFIG API (Account-level Configuration)
// ============================================================================

export interface FacetCategoryConfigItem {
  id: string
  categoryId: string
  label: string
  description?: string | null
  color: string
  icon: string
  sortOrder: number
}

export interface FacetTypeMappingItem {
  id: string
  facetTypeId: string
  categoryId: string
  label: string
  description?: string | null
  color?: string | null
  icon?: string | null
  createsEntity: boolean
  defaultEntityType?: string | null
  allowedEntityTypes?: string[] | null
  isCustom: boolean
  sortOrder: number
}

// API returns categories/mappings as objects keyed by ID
export interface FacetConfigResponse {
  categories: Record<string, FacetCategoryConfigItem>
  mappings: Record<string, FacetTypeMappingItem>
  isCustomized: boolean
}

export const facetConfigApi = {
  /**
   * Get facet configuration for a project
   * Note: Facet config is project-scoped, not account-scoped
   * Returns default configuration structure for compatibility with useFacetConfig hook
   */
  async get(_accountId: string, _projectId: string): Promise<FacetConfigResponse> {
    // Return empty config with defaults - the hook will merge with static config
    return {
      categories: {},
      mappings: {},
      isCustomized: false,
    }
  },

  /** Get default configuration (for comparison/reset) */
  async getDefaults(_accountId: string, _projectId: string): Promise<FacetConfigResponse> {
    // Return defaults - UI will merge with static config
    return {
      categories: {},
      mappings: {},
      isCustomized: false,
    }
  },

  /** Initialize project with default configuration */
  async initialize(_accountId: string, _projectId: string): Promise<FacetConfigResponse> {
    // Return empty config - UI uses static defaults from interviewer.ts
    return {
      categories: {},
      mappings: {},
      isCustomized: false,
    }
  },

  /** Update category configuration (no-op - not implemented in database) */
  async updateCategories(_accountId: string, _projectId: string, _categories: Array<{
    categoryId: string
    label: string
    description?: string | null
    color: string
    icon: string
    sortOrder: number
  }>): Promise<FacetCategoryConfigItem[]> {
    // Categories are static in interviewer.ts - no database persistence
    return []
  },

  /** Update facet type mappings (no-op - not implemented in database) */
  async updateMappings(_accountId: string, _projectId: string, _mappings: Array<{
    facetTypeId: string
    categoryId: string
    label: string
    description?: string | null
    color?: string | null
    icon?: string | null
    createsEntity: boolean
    defaultEntityType?: string | null
    allowedEntityTypes?: string[] | null
    isCustom?: boolean
    sortOrder: number
  }>): Promise<FacetTypeMappingItem[]> {
    // Mappings are static in interviewer.ts - no database persistence
    // The database only stores enabled/customName/customColor per facet type
    return []
  },

  /** Reset project configuration to defaults */
  async reset(_accountId: string, _projectId: string): Promise<FacetConfigResponse> {
    // Return defaults - UI will use static config
    return {
      categories: {},
      mappings: {},
      isCustomized: false,
    }
  },
}

// ============================================================================
// SPARQL API (PROXIED THROUGH BACKEND)
// ============================================================================

export const sparqlApi = {
  async query(accountId: string, projectId: string, sparqlQuery: string): Promise<any> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/query`, {
      method: 'POST',
      body: JSON.stringify({ query: sparqlQuery }),
    })
  },

  async update(accountId: string, projectId: string, sparqlUpdate: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/update`, {
      method: 'POST',
      body: JSON.stringify({ update: sparqlUpdate }),
    })
  },

  async ask(accountId: string, projectId: string, sparqlQuery: string): Promise<boolean> {
    const result = await apiFetch<{ boolean: boolean }>(`/api/accounts/${accountId}/projects/${projectId}/sparql/query`, {
      method: 'POST',
      body: JSON.stringify({ query: sparqlQuery }),
    })
    return result.boolean
  },

  /** Create an entity in the project graph with proper typing */
  async createEntity(
    accountId: string,
    projectId: string,
    data: {
      entityType: string
      label: string
      description?: string
    }
  ): Promise<{ entityUri: string; entityType: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ============================================================================
// ENTITY TREE API - Entity validation and tree view
// ============================================================================

export interface EntityTreeInstance {
  uri: string
  label: string
  classUri: string
  className: string
  domain: 'market' | 'business' | 'solution'
  status: 'pending' | 'confirmed' | 'rejected'
  createdAt: string
  source: 'ai' | 'manual' | 'import'
  confidence?: number
}

export interface EntityTreeClassGroup {
  classUri: string
  className: string
  domain: 'market' | 'business' | 'solution'
  instances: EntityTreeInstance[]
}

export interface EntityTreeResponse {
  entities: EntityTreeInstance[]
  byDomain: {
    market: EntityTreeClassGroup[]
    business: EntityTreeClassGroup[]
    solution: EntityTreeClassGroup[]
  }
  counts: {
    total: number
    pending: number
    confirmed: number
    rejected: number
  }
}

export const entityTreeApi = {
  /** Get all entities for tree view */
  async getTree(
    accountId: string,
    projectId: string
  ): Promise<EntityTreeResponse> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities/tree`)
  },

  /** Update entity validation status */
  async updateStatus(
    accountId: string,
    projectId: string,
    entityUri: string,
    status: 'pending' | 'confirmed' | 'rejected'
  ): Promise<{ success: boolean; entityUri: string; status: string }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/sparql/entities/${encodeURIComponent(entityUri)}/status`,
      {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }
    )
  },

  /** Bulk validate entities */
  async bulkValidate(
    accountId: string,
    projectId: string,
    entityUris: string[],
    status: 'confirmed' | 'rejected'
  ): Promise<{ success: boolean; updated: number; errors: string[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities/validate`, {
      method: 'POST',
      body: JSON.stringify({ entityUris, status }),
    })
  },

  /** Get count of pending entities */
  async getPendingCount(
    accountId: string,
    projectId: string
  ): Promise<{ count: number }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities/pending-count`)
  },

  /** Create a new entity (manual/hybrid mode) */
  async createEntity(
    accountId: string,
    projectId: string,
    entityType: string,
    label: string,
    description?: string
  ): Promise<{ entityUri: string; entityType: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities`, {
      method: 'POST',
      body: JSON.stringify({ entityType, label, description }),
    })
  },

  /** Get AI suggestions for an entity */
  async getSuggestions(
    accountId: string,
    projectId: string,
    entityUri: string,
    phaseContext?: PhaseContextForSuggestions
  ): Promise<EntitySuggestionsResponse> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/sparql/entities/suggestions`, {
      method: 'POST',
      body: JSON.stringify({ entityUri, phaseContext }),
    })
  },
}

// Phase context for suggestions (structured mode)
export interface PhaseContextForSuggestions {
  phaseName: string
  phaseDescription?: string
  requiredEntities?: Array<{
    classUri: string
    className: string
    domain: 'market' | 'business' | 'solution'
    minCount: number
    currentCount: number
  }>
}

// Entity suggestion types
export interface EntitySuggestion {
  id: string
  type: 'related' | 'refinement' | 'gap'
  name: string
  reason: string
  confidence: number
  entityType?: string
  domain?: 'market' | 'business' | 'solution'
}

export interface EntitySuggestionsResponse {
  suggestions: EntitySuggestion[]
  entityContext: {
    uri: string
    label: string
    entityType: string
    domain: 'market' | 'business' | 'solution'
    relatedEntities?: Array<{
      uri: string
      label: string
      entityType: string
      domain: 'market' | 'business' | 'solution'
      relationship: string
    }>
  }
}

// ============================================================================
// DOCUMENTS2 API - Entity-First Extraction
// ============================================================================

export interface Document2Entity {
  id: string
  tempId: string
  entityType: string
  name: string
  description: string | null
  confidence: number
  graphUri: string | null
  status: string
  createdAt: string
  evidence: Array<{
    id: string
    quote: string
    positionStart: number | null
    positionEnd: number | null
  }>
}

export interface Document2Relationship {
  id: string
  fromEntityId: string
  toEntityId: string
  relationshipType: string
  evidence: string | null
  confidence: number
  status: string
}

export interface Document2WithEntities {
  document: Source
  entities: Document2Entity[]
  relationships: Document2Relationship[]
}

export interface Document2ListItem {
  id: string
  title: string
  contentType: ContentType
  status: string
  analyzedAt: string | null
  createdAt: string
  entityCount: number
  relationshipCount: number
}

export const documents2Api = {
  async list(accountId: string, projectId: string): Promise<Document2ListItem[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2`)
  },

  async get(accountId: string, projectId: string, docId: string): Promise<Document2WithEntities> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}`)
  },

  async create(accountId: string, projectId: string, data: {
    title: string
    contentType: ContentType
    rawContent: string
    markdownContent?: string
    fileName?: string
    fileType?: 'pdf' | 'md' | 'txt'
  }): Promise<Source> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async saveEntities(accountId: string, projectId: string, docId: string, data: {
    entities: Array<{
      id: string
      type: string
      name: string
      description?: string
      confidence?: number
      evidence: Array<{ quote: string; position?: number }>
    }>
    relationships?: Array<{
      from: string
      to: string
      type: string
      evidence?: string
      confidence?: number
    }>
  }): Promise<{
    message: string
    entitiesCount: number
    relationshipsCount: number
    tempIdMapping: Record<string, string>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/entities`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async createAllGraphEntities(accountId: string, projectId: string, docId: string): Promise<{
    message: string
    entitiesCreated: number
    relationshipsCreated: number
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/create-all-graph-entities`, {
      method: 'POST',
    })
  },

  async createGraphEntities(accountId: string, projectId: string, docId: string, entityIds: string[]): Promise<{
    message: string
    entitiesCreated: number
    relationshipsCreated: number
    uriMapping: Record<string, string>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/create-graph-entities`, {
      method: 'POST',
      body: JSON.stringify({ entityIds }),
    })
  },

  async deleteEntity(accountId: string, projectId: string, docId: string, entityId: string): Promise<void> {
    await apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/entities/${entityId}`, {
      method: 'DELETE',
    })
  },

  async updateEntity(accountId: string, projectId: string, docId: string, entityId: string, data: {
    status?: string
    name?: string
    description?: string
    entityType?: string
  }): Promise<Document2Entity> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/entities/${entityId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  /** Analyze content using Claude API (proxied through backend to bypass CORS) */
  async analyze(accountId: string, projectId: string, data: {
    apiKey: string
    content: string
    contentType: ContentType
    maxTokens?: number
  }): Promise<{
    content: Array<{ type: 'text'; text: string }>
    usage: { input_tokens: number; output_tokens: number }
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/analyze`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Analyze with caching support - checks cache before calling Claude API */
  async analyzeWithCache(accountId: string, projectId: string, data: {
    sourceId: string
    apiKey: string
    content: string
    contentType: ContentType
    maxTokens?: number
    useCache?: boolean
  }): Promise<{
    cached: boolean
    content: Array<{ type: 'text'; text: string }>
    usage?: { input_tokens: number; output_tokens: number }
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/analyze-cached`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Invalidate cache for a specific document */
  async invalidateCache(accountId: string, projectId: string, docId: string): Promise<{
    success: boolean
    message: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/${docId}/cache`, {
      method: 'DELETE',
    })
  },

  /** Get cache statistics for the project */
  async getCacheStats(accountId: string, projectId: string): Promise<{
    total: number
    cached: number
    uncached: number
    cacheHitRate: number
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents2/cache-stats`, {
      method: 'GET',
    })
  },
}

// ============================================================================
// DOCUMENTS3 API - Experimental entity extraction with workflow integration
// ============================================================================

export interface Document3Entity {
  id: string
  type: string
  name: string
  description?: string
  confidence?: number
  evidence: Array<{ quote: string; position?: number }>
}

export interface Document3Relationship {
  from: string
  to: string
  type: string
  evidence?: string
  confidence?: number
}

export interface Document3ExtractionMetadata {
  model: string
  provider: string
  inputTokens: number
  outputTokens: number
  durationMs: number
  usedFallback: boolean
}

export interface Document3ExtractionResult {
  success: boolean
  entities: Document3Entity[]
  relationships: Document3Relationship[]
  metadata: Document3ExtractionMetadata
  error?: string
}

export const documents3Api = {
  async list(accountId: string, projectId: string): Promise<Document2ListItem[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3`)
  },

  async get(accountId: string, projectId: string, docId: string): Promise<Document2WithEntities> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/${docId}`)
  },

  async create(accountId: string, projectId: string, data: {
    title: string
    contentType: ContentType
    rawContent: string
    markdownContent?: string
    fileName?: string
    fileType?: 'pdf' | 'md' | 'txt'
    fileData?: string  // Base64 encoded file data
  }): Promise<Source> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Analyze content using entity extraction service */
  async analyze(accountId: string, projectId: string, data: {
    apiKey: string
    content: string
    contentType: ContentType
  }): Promise<Document3ExtractionResult> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/analyze`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Store entities directly in Fuseki graph (graph-only architecture) */
  async store(accountId: string, projectId: string, docId: string, data: {
    entities: Document3Entity[]
    relationships?: Document3Relationship[]
  }): Promise<{
    success: boolean
    entitiesCreated: number
    evidenceCreated: number
    relationshipsCreated: number
    entityUris: Record<string, string>
    skipped: Array<{ id: string; reason: string }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/${docId}/store`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Get PDF file data for a document */
  async getFileData(accountId: string, projectId: string, docId: string): Promise<{
    fileData: string  // Base64 encoded
    fileType: 'pdf' | 'md' | 'txt' | null
    title: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/${docId}/file`)
  },

  /** Delete a single document and all its entities/evidence */
  async delete(accountId: string, projectId: string, docId: string): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3/${docId}`, {
      method: 'DELETE',
    })
  },

  /** Delete all documents in the project (requires confirmation) */
  async deleteAll(accountId: string, projectId: string): Promise<{
    success: boolean
    deletedCount: number
    message: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/documents3?confirm=true`, {
      method: 'DELETE',
    })
  },
}

// ============================================================================
// DOCS API (Artifact-based)
// New endpoint that stores docs as artifacts instead of sources
// ============================================================================

export const docsApi = {
  async list(accountId: string, projectId: string): Promise<Document2ListItem[]> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs`)
  },

  async get(accountId: string, projectId: string, docId: string): Promise<Document2WithEntities> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}`)
  },

  async create(accountId: string, projectId: string, data: {
    title: string
    contentType: ContentType
    rawContent: string
    markdownContent?: string
    fileName?: string
    fileType?: 'pdf' | 'md' | 'txt'
    fileData?: string
    domain?: 'Market' | 'Business' | 'Solution'
  }): Promise<Source> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async analyze(accountId: string, projectId: string, data: {
    apiKey: string
    content: string
    contentType: ContentType
    fileData?: string // Base64-encoded PDF for fallback detection
    fileName?: string
    enableVisualAnalysis?: boolean // Explicit override
    images?: Array<{  // Pre-rendered page images
      pageNumber: number
      dataUrl: string
      base64Data: string
      mediaType: string
    }>
  }): Promise<Document3ExtractionResult> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/analyze`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async storeEntities(accountId: string, projectId: string, docId: string, data: {
    entities: ExtractedEntity3[]
    relationships?: ExtractedRelationship[]
  }): Promise<{
    success: boolean
    entitiesCreated: number
    evidenceCreated: number
    relationshipsCreated: number
    entityUris: Record<string, string>
    skipped: Array<{ id: string; reason: string }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}/store`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getFile(accountId: string, projectId: string, docId: string): Promise<{
    fileData: string
    fileType: 'pdf' | 'md' | 'txt' | null
    title: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}/file`)
  },

  async delete(accountId: string, projectId: string, docId: string): Promise<{ success: boolean; message: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}`, {
      method: 'DELETE',
    })
  },

  async deleteAll(accountId: string, projectId: string): Promise<{
    success: boolean
    deletedCount: number
    message: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs?confirm=true`, {
      method: 'DELETE',
    })
  },

  async generateContent(accountId: string, projectId: string, docId: string, data: {
    apiKey: string
    templateId: string
    documentTitle?: string
    sectionIds?: string[]
    includeEntities?: boolean
  }): Promise<{
    success: boolean
    sections: Array<{
      sectionId: string
      heading: string
      content: string
      embeddedEntities: Array<{
        label: string
        classUri: string
        className: string
        domain: 'market' | 'business' | 'solution'
        position: { start: number; end: number }
      }>
    }>
    tiptapContent: {
      type: 'doc'
      content: Array<{
        type: string
        attrs?: Record<string, unknown>
        content?: Array<{
          type: string
          text?: string
          marks?: Array<{
            type: string
            attrs?: Record<string, unknown>
          }>
        }>
      }>
    }
    plainText: string
    entityCount: number
    error?: string
  }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/docs/${docId}/generate`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },
}

// ============================================================================
// DISCOVERY ASSISTANT API
// ============================================================================

export interface DiscoverySession {
  id: string
  projectId: string
  userId: string
  title: string
  mode: 'quick' | 'deep'
  status: 'active' | 'paused' | 'completed' | 'abandoned'
  currentPhase: 'market_reality' | 'competitive_landscape' | 'value_design' | 'business_viability'
  currentQuestion: string
  ideaText?: string | null
  completedQuestions: string[]
  createdAt: string
  updatedAt: string
}

export interface DiscoveryMessage {
  id: string
  sessionId: string
  role: 'assistant' | 'user' | 'system'
  content: string
  messageType: 'question' | 'answer' | 'follow_up' | 'pre_populated' | 'quality_feedback' | 'transition'
  questionNumber?: string | null
  metadata?: string | null
  createdAt: string
}

export interface DiscoveryAnswer {
  id: string
  sessionId: string
  questionNumber: string
  answer: string
  qualityScore?: 'shallow' | 'assumed' | 'anecdotal' | 'validated' | 'bulletproof' | null
  confidencePercent?: string | null
  extractedFacets?: string | null
  suggestedEntityType?: string | null
  suggestedEntityValue?: string | null
  isValidated: boolean
  createdAt: string
  updatedAt: string
}

export interface DiscoverySource {
  id: string
  sessionId: string
  sourceType: 'document' | 'url' | 'text'
  title: string
  content?: string | null
  analyzedFacets?: string | null
  createdAt: string
}

export const discoveryApi = {
  // Session management
  async listSessions(accountId: string, projectId: string): Promise<DiscoverySession[]> {
    const result = await apiFetch<{ sessions: DiscoverySession[] }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions`
    )
    return result.sessions
  },

  async createSession(accountId: string, projectId: string, data: {
    title?: string
    mode?: 'quick' | 'deep'
    ideaText?: string
  }): Promise<DiscoverySession> {
    const result = await apiFetch<{ session: DiscoverySession }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.session
  },

  async getSession(accountId: string, projectId: string, sessionId: string): Promise<{
    session: DiscoverySession
    messages: DiscoveryMessage[]
    answers: DiscoveryAnswer[]
    sources: DiscoverySource[]
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}`
    )
  },

  async updateSession(accountId: string, projectId: string, sessionId: string, data: {
    title?: string
    status?: 'active' | 'paused' | 'completed' | 'abandoned'
    currentPhase?: 'market_reality' | 'competitive_landscape' | 'value_design' | 'business_viability'
    currentQuestion?: string
  }): Promise<DiscoverySession> {
    const result = await apiFetch<{ session: DiscoverySession }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
    return result.session
  },

  async deleteSession(accountId: string, projectId: string, sessionId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}`,
      { method: 'DELETE' }
    )
  },

  // Messages
  async addMessage(accountId: string, projectId: string, sessionId: string, data: {
    role: 'assistant' | 'user' | 'system'
    content: string
    messageType: 'question' | 'answer' | 'follow_up' | 'pre_populated' | 'quality_feedback' | 'transition'
    questionNumber?: string
    metadata?: string
  }): Promise<DiscoveryMessage> {
    const result = await apiFetch<{ message: DiscoveryMessage }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.message
  },

  // Answers
  async saveAnswer(accountId: string, projectId: string, sessionId: string, data: {
    questionNumber: string
    answer: string
    qualityScore?: 'shallow' | 'assumed' | 'anecdotal' | 'validated' | 'bulletproof'
    confidencePercent?: string
    extractedFacets?: string
    suggestedEntityType?: string
    suggestedEntityValue?: string
    isValidated?: boolean
  }): Promise<DiscoveryAnswer> {
    const result = await apiFetch<{ answer: DiscoveryAnswer }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/answers`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.answer
  },

  // Sources
  async addSource(accountId: string, projectId: string, sessionId: string, data: {
    sourceType: 'document' | 'url' | 'text'
    title: string
    content?: string
    analyzedFacets?: string
  }): Promise<DiscoverySource> {
    const result = await apiFetch<{ source: DiscoverySource }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/sources`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
    return result.source
  },

  async deleteSource(accountId: string, projectId: string, sessionId: string, sourceId: string): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/sources/${sourceId}`,
      { method: 'DELETE' }
    )
  },

  // Advance to next question
  async advanceSession(accountId: string, projectId: string, sessionId: string): Promise<{
    session: DiscoverySession
    isComplete: boolean
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/advance`,
      { method: 'POST' }
    )
  },

  // ============================================================================
  // DISCOVERY COACH API (Workflow-driven prompts)
  // ============================================================================

  // Get accumulated context for a session
  async getContext(accountId: string, projectId: string, sessionId: string): Promise<{
    context: DiscoveryContext
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/context`
    )
  },

  // Execute a prompt with context
  async executePrompt(accountId: string, projectId: string, sessionId: string, data: {
    stepKey: string
    promptType: 'question' | 'assess' | 'followup' | 'extract'
    userInput?: string
    apiKey: string
  }): Promise<{
    result: unknown
    text: string
    usage?: { inputTokens: number; outputTokens: number }
    context?: {
      previousSummary: string
      capturedEntities: number
      knowledgeGaps: string[]
    }
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/execute-prompt`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // Save answer with optional extraction
  async saveStepAnswer(accountId: string, projectId: string, sessionId: string, stepKey: string, data: {
    answer: string
    assessment?: { quality: string; confidence: number }
    apiKey?: string
  }): Promise<{
    answer: DiscoveryAnswer
    extractedEntities?: unknown
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/steps/${stepKey}/answer`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // Assemble a prompt without executing (for preview/debugging)
  async assemblePrompt(accountId: string, projectId: string, sessionId: string, data: {
    stepKey: string
    promptType: 'question' | 'assess' | 'followup' | 'extract'
    userInput?: string
  }): Promise<{
    promptKey: string
    assembled: {
      systemPrompt: string
      userPrompt: string
      model: string
      temperature: number
      maxTokens: number
    }
    context: {
      previousSummary: string
      capturedEntities: number
      knowledgeGaps: string[]
    }
  }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/assemble-prompt`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  // ============================================================================
  // CONVERSATIONAL COACH (New natural conversation approach)
  // ============================================================================

  /**
   * Get messages for a discovery session
   */
  async getMessages(
    accountId: string,
    projectId: string,
    sessionId: string
  ): Promise<Array<{
    id: string
    role: 'user' | 'assistant' | 'system'
    content: string
    timestamp: string
    metadata?: Record<string, unknown>
  }>> {
    const result = await apiFetch<{
      messages: Array<{
        id: string
        role: 'user' | 'assistant' | 'system'
        content: string
        timestamp: string
        metadata?: Record<string, unknown>
      }>
    }>(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/messages`
    )
    return result.messages
  },

  /**
   * Delete all messages for a discovery session
   */
  async deleteMessages(
    accountId: string,
    projectId: string,
    sessionId: string
  ): Promise<void> {
    await apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/messages`,
      { method: 'DELETE' }
    )
  },

  /**
   * Export messages for a discovery session (returns download URL)
   */
  getExportUrl(
    accountId: string,
    projectId: string,
    sessionId: string,
    format: 'json' | 'markdown' = 'markdown'
  ): string {
    return `${API_URL}/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/messages/export?format=${format}`
  },

  /**
   * Send a conversational chat message (streaming)
   */
  async chatStream(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: {
      message: string
      mode?: 'guided' | 'organic'
      apiKey: string
      model?: string
    },
    onEvent: (event: ConversationalChatEvent) => void
  ): Promise<void> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    const token = getSessionToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(
      `${API_URL}/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/chat/stream`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      }
    )

    if (!response.ok) {
      throw new Error(`Chat stream failed: ${response.statusText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''
    let currentEventType = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEventType = line.slice(6).trim()
          continue
        }
        if (line.startsWith('data:')) {
          try {
            const data = JSON.parse(line.slice(5).trim())
            // Include the event type in the event object
            onEvent({ type: currentEventType, ...data })
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
  },

  /**
   * Send a conversational chat message (non-streaming)
   */
  async chat(
    accountId: string,
    projectId: string,
    sessionId: string,
    data: {
      message: string
      mode?: 'guided' | 'organic'
      apiKey: string
      model?: string
    }
  ): Promise<ConversationalChatResult> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/chat`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /**
   * Get topic coverage for a session
   */
  async getTopicCoverage(
    accountId: string,
    projectId: string,
    sessionId: string,
    mode?: 'guided' | 'organic'
  ): Promise<TopicCoverageResponse> {
    const queryParams = mode ? `?mode=${mode}` : ''
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/coverage${queryParams}`
    )
  },

  /**
   * Initialize topic coverage for a new session
   */
  async initializeTopicCoverage(
    accountId: string,
    projectId: string,
    sessionId: string
  ): Promise<{ success: boolean; coverage: Record<string, TopicCoverage> }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/discovery/sessions/${sessionId}/coverage/initialize`,
      { method: 'POST' }
    )
  },
}

// Conversational Coach Types
export type ConversationMode = 'guided' | 'organic'

export interface TopicCoverage {
  topicId: string
  level: 'not_started' | 'mentioned' | 'explored' | 'validated'
  confidence: number
  keyInsights: string[]
  extractedEntities: Array<{ type: string; label: string; description?: string }>
  signalsDetected: string[]
  lastUpdated: string
}

export interface DiscoveryTopic {
  id: string
  name: string
  description: string
  category: string
  color: string
  icon: string
}

export interface TopicCoverageResponse {
  topics: Array<{
    topic: DiscoveryTopic
    coverage: TopicCoverage | null
  }>
  suggestedNext?: string
  overallProgress: number
}

export interface TagOption {
  id: string
  label: string
  description?: string
  category?: string
}

export interface TagSelection {
  type: 'tags'
  question: string
  options: TagOption[]
  multiSelect: boolean
  allowOther: boolean
  minSelect?: number
  maxSelect?: number
}

export interface ConversationalChatEvent {
  type: 'text' | 'quick_replies' | 'tag_selection' | 'topic_update' | 'entity_extracted' | 'done' | 'error'
  content?: string
  replies?: string[]
  tagSelection?: TagSelection
  topicId?: string
  level?: string
  confidence?: number
  insights?: string[]
  entity?: { type: string; label: string; description?: string }
  messageId?: string
  topicCoverage?: Record<string, TopicCoverage>
  suggestedNextTopic?: string
  message?: string
  code?: string
}

export interface ConversationalChatResult {
  messageId: string
  content: string
  quickReplies: string[]
  topicCoverage: Record<string, TopicCoverage>
  extractedEntities: Array<{ type: string; label: string; description?: string }>
  suggestedNextTopic?: string
}

// ============================================================================
// WORKFLOW API
// ============================================================================

export interface WorkflowStep {
  key: string
  name: string
  order: number
  phase: string
  primaryEntity: string | null
  facetTypes: string[]
  qualitySignals: string[]
  followUpRules: Record<string, string>
}

export interface WorkflowPhase {
  key: string
  label: string
  color: string
  questions: number[]
}

export interface DiscoveryWorkflow {
  workflow: {
    id: string
    key: string
    name: string
    description: string
  }
  steps: WorkflowStep[]
  phases: WorkflowPhase[]
}

export const workflowApi = {
  // Get the Discovery workflow with all steps
  async getDiscoveryWorkflow(): Promise<DiscoveryWorkflow> {
    return apiFetch('/api/discovery/workflow')
  },
}

// Discovery Context type
export interface DiscoveryContext {
  sessionId: string
  projectId: string
  ideaStatement: string | null
  currentStep: number
  currentPhase: string
  answers: Array<{
    stepKey: string
    questionNumber: string
    question: string
    answer: string
    quality: string | null
    confidence: number | null
  }>
  capturedEntities: Array<{
    type: string
    label: string
    description: string | null
  }>
  uploadedSources: Array<{
    id: string
    title: string
    contentType: string
    facetCount: number
  }>
  previousSummary: string
  knowledgeGaps: string[]
}

// ============================================================================
// ENTITY RULES API (Semantic Validation Rules)
// ============================================================================

export interface EntityRule {
  id: string
  accountId: string
  entityClass: string
  entityDomain: string
  name: string
  description: string | null
  category: 'validation' | 'inference' | 'classification' | 'propagation'
  antecedent: string
  consequent: string
  swrlSyntax: string | null
  isEnabled: boolean
  isSystemRule: boolean
  severity: 'error' | 'warning' | 'info'
  example: string | null
  createdAt: string
  updatedAt: string
}

export interface GroupedRules {
  entityClass: string
  entityDomain: string
  rules: EntityRule[]
}

export const entityRulesApi = {
  /** Get all rules for account (grouped by entity class) */
  async list(accountId: string): Promise<{ rules: EntityRule[]; grouped: GroupedRules[] }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules`)
  },

  /** Get rules for specific entity class */
  async getByEntity(accountId: string, entityClass: string): Promise<{ rules: EntityRule[] }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules/${encodeURIComponent(entityClass)}`)
  },

  /** Create a new custom rule */
  async create(accountId: string, data: {
    entityClass: string
    entityDomain: string
    name: string
    description?: string
    category: 'validation' | 'inference' | 'classification' | 'propagation'
    antecedent: string
    consequent: string
    swrlSyntax?: string
    severity?: 'error' | 'warning' | 'info'
    example?: string
  }): Promise<{ rule: EntityRule }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Update a rule (enable/disable or edit custom rules) */
  async update(accountId: string, ruleId: string, data: {
    isEnabled?: boolean
    name?: string
    description?: string
    antecedent?: string
    consequent?: string
    swrlSyntax?: string
    severity?: 'error' | 'warning' | 'info'
    example?: string
  }): Promise<{ rule: EntityRule }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules/${ruleId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  /** Delete a custom rule (system rules cannot be deleted) */
  async delete(accountId: string, ruleId: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules/${ruleId}`, {
      method: 'DELETE',
    })
  },

  /** Force re-seed system rules (for dev/testing) */
  async reseed(accountId: string): Promise<{ success: boolean; count: number }> {
    return apiFetch(`/api/accounts/${accountId}/entity-rules/seed`, {
      method: 'POST',
    })
  },
}

// ============================================================================
// CANVAS AI API
// ============================================================================

export interface CanvasAIMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  metadata?: Record<string, unknown>
}

export interface CanvasAISectionScore {
  score: number
  feedback?: string
  lastUpdated: string
}

export interface CanvasAIExtractedEntity {
  type: string
  name: string
  properties: Record<string, unknown>
  confidence: 'high' | 'medium' | 'low'
  sourceSpan?: string
  sectionKey: string
}

export interface CanvasAISession {
  id: string
  canvasInstanceId: string
  canvasType: string
  workflowId: string | null
  currentSection: string | null
  sectionScores: Record<string, CanvasAISectionScore>
  conversationHistory: CanvasAIMessage[]
  extractedEntities: CanvasAIExtractedEntity[]
  contextSnapshot: Record<string, unknown>
  projectId: string
  accountId: string
  createdAt: string
  updatedAt: string
}

export interface CanvasAIWorkflow {
  id: string
  key: string
  name: string
  description: string
  canvasType: string
  sectionMapping: Record<string, string>
  color: string
  icon: string
}

export interface CanvasAIValidation {
  score: number
  strengths: string[]
  gaps: string[]
  suggestions: string[]
  feedback?: string
}

export const canvasAIApi = {
  /** Get or create an AI session for a canvas instance */
  async getSession(accountId: string, projectId: string, canvasInstanceId: string, canvasType = 'bmc'): Promise<{
    session: CanvasAISession
    workflow: CanvasAIWorkflow | null
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/session?canvasType=${canvasType}`
    )
  },

  /** Update session state */
  async updateSession(accountId: string, projectId: string, canvasInstanceId: string, data: {
    currentSection?: string
    sectionScores?: Record<string, CanvasAISectionScore>
    contextSnapshot?: Record<string, unknown>
  }): Promise<{ session: CanvasAISession }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/session`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      }
    )
  },

  /** Send a message and get AI response */
  async sendMessage(accountId: string, projectId: string, canvasInstanceId: string, data: {
    message: string
    sectionKey: string
    apiKey: string
    canvasContext?: Record<string, unknown>
  }): Promise<{
    message: CanvasAIMessage
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/message`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /** Get an initial question for a section */
  async getQuestion(accountId: string, projectId: string, canvasInstanceId: string, data: {
    sectionKey: string
    apiKey: string
    canvasContext?: Record<string, unknown>
  }): Promise<{
    question: string
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/question`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /** Validate section content */
  async validateSection(accountId: string, projectId: string, canvasInstanceId: string, data: {
    sectionKey: string
    content: string | string[]
    apiKey: string
  }): Promise<{
    validation: CanvasAIValidation
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/validate`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /** Extract entities from section content */
  async extractEntities(accountId: string, projectId: string, canvasInstanceId: string, data: {
    sectionKey: string
    content: string | string[]
    apiKey: string
  }): Promise<{
    entities: CanvasAIExtractedEntity[]
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/extract`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /** Get context for a canvas */
  async getContext(accountId: string, projectId: string, canvasInstanceId: string): Promise<{
    context: {
      canvasType: string
      currentSection: string | null
      sectionScores: Record<string, CanvasAISectionScore>
      extractedEntities: CanvasAIExtractedEntity[]
      contextSnapshot: Record<string, unknown>
      linkedEntities: {
        personas: unknown[]
        segments: unknown[]
        competitors: unknown[]
        products: unknown[]
        valueProps: unknown[]
      }
    }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/context`
    )
  },

  /** Get suggestions for a section */
  async getSuggestions(accountId: string, projectId: string, canvasInstanceId: string, sectionKey: string): Promise<{
    suggestions: {
      extractedEntities: CanvasAIExtractedEntity[]
      qualityScore: number | null
      feedback: string | null
      relatedEntities: unknown[]
      bestPractices: unknown[]
    }
  }> {
    const encodedCanvasId = encodeURIComponent(canvasInstanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedCanvasId}/ai/suggestions/${sectionKey}`
    )
  },

  /** Generate AI suggestions for a section */
  async generateSuggestions(accountId: string, projectId: string, canvasInstanceId: string, data: {
    sectionKey: string
    apiKey: string
    canvasContext?: Record<string, string[]>
    limit?: number
  }): Promise<{
    suggestions: Array<{
      id: string
      label: string
      description?: string
      entityType: string
      confidence: 'high' | 'medium' | 'low'
      reasoning?: string
    }>
    usage?: { inputTokens: number; outputTokens: number }
  }> {
    // Pass canvasInstanceId in body to avoid URL encoding issues with slashes
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/ai/generate-suggestions`,
      {
        method: 'POST',
        body: JSON.stringify({ ...data, canvasInstanceId }),
      }
    )
  },

  /** Get workflow info for a canvas type */
  async getWorkflow(canvasType: string): Promise<{
    workflow: CanvasAIWorkflow
    steps: Array<{
      key: string
      name: string
      description: string
      icon: string
      color: string
      stepNumber: number
      linkedEntities: string[]
      domain: string
    }>
  }> {
    return apiFetch(`/api/canvas/workflows/${canvasType}`)
  },

  /** List all canvas workflows */
  async listWorkflows(): Promise<{
    workflows: CanvasAIWorkflow[]
  }> {
    return apiFetch('/api/canvas/workflows')
  },
}

// ============================================================================
// INDUSTRY TEMPLATES API
// ============================================================================

export interface IndustryInfo {
  industryKey: string
  industryName: string
  industryDescription: string | null
  industryIcon: string | null
  industryColor: string | null
  canvasTypes: string[]
}

export interface SectionPrefill {
  notes?: string
  suggestedItems?: Array<{
    label: string
    entityType: string
    description?: string
  }>
}

export interface IndustryTemplate {
  id: string
  industryKey: string
  industryName: string
  industryDescription: string | null
  industryIcon: string | null
  industryColor: string | null
  canvasTemplateId: string
  sectionPrefills: Record<string, SectionPrefill>
  exampleContent: Record<string, unknown> | null
}

export const industryTemplatesApi = {
  /** Get all available industries */
  async getIndustries(): Promise<{ industries: IndustryInfo[] }> {
    return apiFetch('/api/canvas/industry-templates')
  },

  /** Get all canvas templates for an industry */
  async getIndustryTemplates(industryKey: string): Promise<{
    industry: {
      industryKey: string
      industryName: string
      industryDescription: string | null
      industryIcon: string | null
      industryColor: string | null
    }
    templates: Array<{
      id: string
      canvasTemplateId: string
      sectionPrefills: Record<string, SectionPrefill>
      exampleContent: Record<string, unknown> | null
    }>
  }> {
    return apiFetch(`/api/canvas/industry-templates/${industryKey}`)
  },

  /** Get a specific industry template */
  async getTemplate(industryKey: string, canvasType: string): Promise<{
    template: IndustryTemplate
  }> {
    return apiFetch(`/api/canvas/industry-templates/${industryKey}/${canvasType}`)
  },
}

// ============================================================================
// VALIDATION API
// ============================================================================

export interface AutoFixAction {
  id: string
  type: 'add_property' | 'link_to_entity' | 'create_and_link_entity' | 'set_enum_value' | 'add_classification'
  label: string
  description: string
  sparqlUpdate: string | null
  requiresUserInput: boolean
  inputSchema?: {
    type: 'entity_picker' | 'property_value' | 'enum_select'
    property?: string
    targetEntityClass?: string
    enumValues?: string[]
    placeholder?: string
  }
}

export interface Violation {
  id: string
  ruleId: string
  projectId: string
  entityUri: string
  entityLabel: string | null
  violationMessage: string | null
  suggestedFix: string | null
  status: 'open' | 'acknowledged' | 'resolved'
  resolvedAt: string | null
  resolvedById: string | null
  createdAt: string
  rule?: EntityRule
  autoFixActions?: AutoFixAction[]
}

export interface ViolationSummary {
  totalViolations: number
  byType: {
    error: number
    warning: number
    info: number
  }
  byStatus: {
    open: number
    acknowledged: number
    resolved: number
  }
  topViolations: Array<{
    ruleName: string
    count: number
    severity: string
  }>
}

export const validationApi = {
  /** Validate a single entity */
  async validateEntity(
    projectId: string,
    entityUri: string,
    entityType?: string
  ): Promise<{
    entityUri: string
    entityLabel: string | null
    violations: Violation[]
    inferredProperties?: Array<{
      property: string
      value: string
      inferredBy: string
    }>
  }> {
    return apiFetch(`/api/projects/${projectId}/validation/validate-entity`, {
      method: 'POST',
      body: JSON.stringify({ entityUri, entityType }),
    })
  },

  /** Get violations for a specific entity */
  async getViolationsByEntity(
    projectId: string,
    entityUri: string
  ): Promise<{ violations: Violation[] }> {
    const encodedUri = encodeURIComponent(entityUri)
    return apiFetch(`/api/projects/${projectId}/validation/violations/${encodedUri}`)
  },

  /** Get all violations for a project */
  async getViolationsByProject(
    projectId: string,
    options?: {
      status?: 'open' | 'acknowledged' | 'resolved'
      severity?: 'error' | 'warning' | 'info'
      limit?: number
      offset?: number
    }
  ): Promise<{ violations: Violation[] }> {
    const params = new URLSearchParams()
    if (options?.status) params.set('status', options.status)
    if (options?.severity) params.set('severity', options.severity)
    if (options?.limit) params.set('limit', options.limit.toString())
    if (options?.offset) params.set('offset', options.offset.toString())

    const query = params.toString() ? `?${params.toString()}` : ''
    return apiFetch(`/api/projects/${projectId}/validation/violations${query}`)
  },

  /** Get violation summary for a project */
  async getViolationSummary(projectId: string): Promise<ViolationSummary> {
    return apiFetch(`/api/projects/${projectId}/validation/summary`)
  },

  /** Get violation counts for multiple entities */
  async getViolationCounts(
    projectId: string,
    entityUris: string[]
  ): Promise<{
    counts: Record<string, { total: number; byType: { error: number; warning: number; info: number } }>
  }> {
    return apiFetch(`/api/projects/${projectId}/validation/violation-counts`, {
      method: 'POST',
      body: JSON.stringify({ entityUris }),
    })
  },

  /** Acknowledge a violation */
  async acknowledgeViolation(
    projectId: string,
    violationId: string,
    userId?: string
  ): Promise<{ violation: Violation }> {
    return apiFetch(`/api/projects/${projectId}/validation/acknowledge/${violationId}`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    })
  },

  /** Reopen a violation */
  async reopenViolation(
    projectId: string,
    violationId: string
  ): Promise<{ violation: Violation }> {
    return apiFetch(`/api/projects/${projectId}/validation/reopen/${violationId}`, {
      method: 'POST',
    })
  },

  /** Apply an auto-fix action */
  async applyAutoFix(
    projectId: string,
    violationId: string,
    actionId: string,
    userInput?: {
      propertyValue?: string
      selectedEntityUri?: string
      selectedEnumValue?: string
    }
  ): Promise<{
    success: boolean
    message: string
    violations: Violation[]
  }> {
    return apiFetch(`/api/projects/${projectId}/validation/auto-fix/${violationId}`, {
      method: 'POST',
      body: JSON.stringify({ actionId, userInput }),
    })
  },

  /** Validate all entities in a project */
  async validateProject(
    projectId: string,
    entityTypes?: string[]
  ): Promise<{
    totalEntities: number
    totalViolations: number
    violationsByType: { error: number; warning: number; info: number }
  }> {
    return apiFetch(`/api/projects/${projectId}/validation/validate-project`, {
      method: 'POST',
      body: JSON.stringify({ entityTypes }),
    })
  },
}

// ============================================================================
// AI ANALYSIS API
// ============================================================================

export interface AnalyzeDocumentRequest {
  sourceId: string
  content: string
  contentType: string
  pageImages?: Array<{
    id: string
    data: string
    mimeType?: string
    pageNumber?: number
    width?: number
    height?: number
  }>
  accountId?: string
  projectId?: string
}

export interface AnalyzeDocumentResponse {
  sourceId: string
  facets: Array<{
    facetTypeId: string
    title?: string
    content: string
    evidence: string
    confidence: number
    positionStart?: number
    positionEnd?: number
    suggestedEntityType?: string
    suggestedReason?: string
  }>
  visualChunks: Array<{
    pageNumber: number
    description: string
    chunkType: string
    textContext?: string
    confidence: number
  }>
  visualFacets: Array<{
    facetTypeId: string
    title: string
    content: string
    evidence: string
    confidence: number
    imageId: string
    isVisual: boolean
  }>
  analysisTime: number
  usage?: {
    inputTokens: number
    outputTokens: number
  }
}

// ============================================================================
// KNOWLEDGE BASE API (RAG)
// ============================================================================

export type KnowledgeSourceType = 'file' | 'url' | 'manual'
export type KnowledgeCategory = 'framework' | 'case_study' | 'best_practice' | 'template' | 'research' | 'guide' | 'book' | 'other'
export type KnowledgeStatus = 'pending' | 'processing' | 'ready' | 'error'

export interface ProcessingProgress {
  step: 'extracting' | 'chunking' | 'embedding' | 'storing' | 'complete' | 'error'
  progress: number // 0-100
  message: string
  totalChunks?: number
  processedChunks?: number
  startedAt?: string
  updatedAt?: string
}

export interface KnowledgeSource {
  id: string
  title: string
  description?: string | null
  sourceType: KnowledgeSourceType
  category?: KnowledgeCategory | null
  url?: string | null
  fileName?: string | null
  status: KnowledgeStatus
  chunkCount: number
  tags: string[]
  createdAt: string
  updatedAt: string
  processedAt?: string | null
  errorMessage?: string | null
  processingProgress?: ProcessingProgress | null
}

export interface KnowledgeQueryResult {
  chunkId: string
  content: string
  similarity: number
  sourceId: string
  sourceTitle: string
  sourceUrl?: string | null
  sourceCategory?: string | null
  topic?: string | null
}

export interface KnowledgeQueryResponse {
  query: string
  results: KnowledgeQueryResult[]
  totalFound: number
  model: string
}

export interface KnowledgeStats {
  totalSources: number
  totalChunks: number
  sourcesByStatus: Record<KnowledgeStatus, number>
  sourcesByCategory: Record<string, number>
  recentQueries: number
}

// PM Topics for Training Guide
export interface PMTopic {
  id: string
  name: string
  description: string
  icon: string
  color: 'market' | 'business' | 'solution'
  keywords: string[]
  chunkCount: number
}

export interface TopicContentResponse {
  topic: Omit<PMTopic, 'chunkCount'> | undefined
  sources: Array<{
    sourceId: string
    sourceTitle: string
    sourceUrl: string | null
    sourceCategory: string | null
    chunks: Array<{
      id: string
      content: string
      chunkIndex: number
      subtopic: string | null
    }>
  }>
  totalChunks: number
}

// AI-synthesized training guide
export interface GuideSection {
  title: string
  content: string
  keyPoints: string[]
}

export interface TopicGuide {
  id: string
  topicId: string
  title: string
  overview: string
  sections: GuideSection[]
  generatedAt: string
  sourceChunkCount: number
  version: number
  isStale: boolean
}

export const knowledgeApi = {
  /** List all knowledge sources */
  async listSources(accountId: string, options?: {
    category?: KnowledgeCategory
    status?: KnowledgeStatus
  }): Promise<{ sources: KnowledgeSource[]; total: number }> {
    const params = new URLSearchParams()
    if (options?.category) params.set('category', options.category)
    if (options?.status) params.set('status', options.status)
    const query = params.toString() ? `?${params.toString()}` : ''
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources${query}`)
  },

  /** Add a URL source */
  async addUrl(accountId: string, data: {
    url: string
    title?: string
    category?: KnowledgeCategory
    tags?: string[]
    description?: string
  }): Promise<{ source: KnowledgeSource }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources/url`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Add manual content */
  async addManual(accountId: string, data: {
    title: string
    content: string
    category?: KnowledgeCategory
    tags?: string[]
    description?: string
  }): Promise<{ source: KnowledgeSource }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources/manual`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Upload a file (PDF, TXT, MD, DOCX) */
  async uploadFile(accountId: string, data: {
    file: File
    title?: string
    category?: KnowledgeCategory
    tags?: string[]
    description?: string
  }): Promise<{ source: KnowledgeSource }> {
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.title) formData.append('title', data.title)
    if (data.category) formData.append('category', data.category)
    if (data.description) formData.append('description', data.description)
    if (data.tags?.length) formData.append('tags', data.tags.join(','))

    const token = getSessionToken()
    const headers: Record<string, string> = {}
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    // Note: Don't set Content-Type for FormData - browser sets it with boundary

    const response = await fetch(`${API_URL}/api/accounts/${accountId}/knowledge/sources/file`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: formData,
    })

    const result = await response.json()
    if (!response.ok) {
      throw new Error(result.error || result.message || `Upload failed: ${response.status}`)
    }
    return result
  },

  /** Process a source (generate embeddings) */
  async processSource(accountId: string, sourceId: string, data?: {
    // Unified embedding config
    provider?: 'openai' | 'mistral' | 'ollama' | 'voyage'
    apiKey?: string
    baseUrl?: string
    // Legacy support (deprecated)
    openaiApiKey?: string
    voyageApiKey?: string
  }): Promise<{ message: string; sourceId: string; status: string }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources/${sourceId}/process`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    })
  },

  /** Delete a source */
  async deleteSource(accountId: string, sourceId: string): Promise<{ message: string }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/sources/${sourceId}`, {
      method: 'DELETE',
    })
  },

  /** Query the knowledge base */
  async query(accountId: string, data: {
    q: string
    topK?: number
    minSimilarity?: number
    category?: KnowledgeCategory
  }, headers?: {
    openaiApiKey?: string
    voyageApiKey?: string
  }): Promise<KnowledgeQueryResponse> {
    const params = new URLSearchParams({ q: data.q })
    if (data.topK) params.set('topK', data.topK.toString())
    if (data.minSimilarity) params.set('minSimilarity', data.minSimilarity.toString())
    if (data.category) params.set('category', data.category)

    const requestHeaders: Record<string, string> = {}
    if (headers?.openaiApiKey) requestHeaders['X-OpenAI-API-Key'] = headers.openaiApiKey
    if (headers?.voyageApiKey) requestHeaders['X-Voyage-API-Key'] = headers.voyageApiKey

    return apiFetch(`/api/accounts/${accountId}/knowledge/query?${params.toString()}`, {
      headers: requestHeaders,
    })
  },

  /** Submit feedback on a query result */
  async submitFeedback(accountId: string, data: {
    chunkId: string
    query: string
    helpful: boolean
    comment?: string
  }): Promise<{ feedback: { id: string } }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/feedback`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /** Get knowledge base stats */
  async getStats(accountId: string): Promise<KnowledgeStats> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/stats`)
  },

  /** Get all PM topics with content counts */
  async getTopics(accountId: string): Promise<{ topics: PMTopic[] }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics`)
  },

  /** Get content for a specific topic */
  async getTopicContent(
    accountId: string,
    topicId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<TopicContentResponse> {
    const params = new URLSearchParams()
    if (options?.limit) params.set('limit', options.limit.toString())
    if (options?.offset) params.set('offset', options.offset.toString())
    const query = params.toString() ? `?${params.toString()}` : ''
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/${topicId}/content${query}`)
  },

  /** Backfill topic classification for unclassified chunks */
  async backfillTopics(
    accountId: string,
    batchSize?: number
  ): Promise<{
    message: string
    classified: number
    unmatched: number
    processed: number
    hasMore: boolean
  }> {
    const query = batchSize ? `?batchSize=${batchSize}` : ''
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/backfill${query}`, {
      method: 'POST',
    })
  },

  /** Get synthesized guide for a topic */
  async getTopicGuide(
    accountId: string,
    topicId: string
  ): Promise<{ guide: TopicGuide | null; exists: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/${topicId}/guide`)
  },

  /** Generate or regenerate a guide for a topic */
  async generateTopicGuide(
    accountId: string,
    topicId: string,
    claudeApiKey: string
  ): Promise<{ guide: TopicGuide; message: string }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/${topicId}/guide/generate`, {
      method: 'POST',
      headers: {
        'X-Claude-API-Key': claudeApiKey,
      },
    })
  },

  /** Delete a topic guide */
  async deleteTopicGuide(accountId: string, topicId: string): Promise<{ message: string }> {
    return apiFetch(`/api/accounts/${accountId}/knowledge/topics/${topicId}/guide`, {
      method: 'DELETE',
    })
  },
}

// ============================================================================
// CANVAS COACH API (Conversational Canvas Coaching)
// ============================================================================

export type CanvasType =
  | 'bmc'
  | 'vpc'
  | 'positioning'
  | 'psc'
  | 'persona'

export type CanvasCoverageLevel = 'not_started' | 'mentioned' | 'explored' | 'validated'

export interface CanvasSectionDefinition {
  id: string
  name: string
  description: string
  canvasType: CanvasType
  color: string
  icon: string
  order: number
}

export interface CanvasSectionCoverage {
  sectionId: string
  level: CanvasCoverageLevel
  confidence: number
  qualityScore?: number
  keyInsights: string[]
  extractedEntities: Array<{
    type: string
    label: string
    description?: string
    sectionId?: string
    confidence: number
  }>
  signalsDetected: string[]
  lastUpdated: Date
}

export interface CanvasCoachSession {
  id: string
  canvasInstanceId: string
  canvasType: CanvasType
  projectId: string
  accountId: string
  createdAt: string
  updatedAt: string
}

export interface CanvasCoverageResponse {
  sections: Array<{
    section: CanvasSectionDefinition
    coverage: CanvasSectionCoverage | null
  }>
  suggestedNext?: string
  overallProgress: number
  canvasType: CanvasType
}

export type QuickActionType = 'add_examples' | 'skip' | 'show_more' | 'navigate_section' | 'text'

export interface QuickAction {
  label: string
  type: QuickActionType
  payload?: string
}

export interface CanvasCoachChatEvent {
  type: 'text' | 'quick_actions' | 'section_update' | 'entity_extracted' | 'suggestion' | 'done' | 'error'
  content?: string
  actions?: QuickAction[]
  sectionId?: string
  level?: string
  confidence?: number
  insights?: string[]
  // For entity_extracted events
  entityType?: string
  label?: string
  description?: string
  message?: string
  messageId?: string
  sectionCoverage?: Record<string, CanvasSectionCoverage>
  suggestedNextSection?: string
  code?: string
}

export interface CanvasCoachChatResult {
  messageId: string
  content: string
  quickActions: QuickAction[]
  sectionCoverage: Record<string, CanvasSectionCoverage>
  extractedEntities: Array<{ type: string; label: string; description?: string; sectionId?: string }>
  suggestedNextSection?: string
}

export interface CanvasDirectEditResponse {
  sectionCoverage: CanvasSectionCoverage
  suggestedNextSection?: string
  suggestionMessage?: string
}

export const canvasCoachApi = {
  /**
   * Create or get a coaching session for a canvas instance
   */
  async getOrCreateSession(
    accountId: string,
    projectId: string,
    instanceId: string,
    canvasType: CanvasType
  ): Promise<{ session: CanvasCoachSession }> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    try {
      // Try to get existing session
      const existing = await apiFetch<{ session: CanvasCoachSession | null }>(
        `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/sessions`
      )
      if (existing.session) {
        return { session: existing.session }
      }
    } catch {
      // Session doesn't exist, create one
    }

    // Create new session
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/sessions`,
      {
        method: 'POST',
        body: JSON.stringify({ canvasType }),
      }
    )
  },

  /**
   * Send a chat message with streaming response
   */
  async chatStream(
    accountId: string,
    projectId: string,
    instanceId: string,
    data: {
      message: string
      mode?: 'guided' | 'organic'
      apiKey: string
      model?: string
      currentSection?: string
      canvasContent?: string
    },
    onEvent: (event: CanvasCoachChatEvent) => void
  ): Promise<void> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    const token = getSessionToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(
      `${API_URL}/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/chat/stream`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      }
    )

    if (!response.ok) {
      throw new Error(`Canvas coach chat stream failed: ${response.statusText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''
    let currentEventType = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEventType = line.slice(6).trim()
          continue
        }
        if (line.startsWith('data:')) {
          try {
            const data = JSON.parse(line.slice(5).trim())
            onEvent({ type: currentEventType as CanvasCoachChatEvent['type'], ...data })
          } catch {
            // Ignore parse errors
          }
        }
      }
    }
  },

  /**
   * Send a chat message (non-streaming)
   */
  async chat(
    accountId: string,
    projectId: string,
    instanceId: string,
    data: {
      message: string
      mode?: 'guided' | 'organic'
      apiKey: string
      model?: string
      currentSection?: string
      canvasContent?: string
    }
  ): Promise<CanvasCoachChatResult> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/chat`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },

  /**
   * Get section coverage for a canvas instance
   */
  async getCoverage(
    accountId: string,
    projectId: string,
    instanceId: string,
    mode: 'guided' | 'organic' = 'organic'
  ): Promise<CanvasCoverageResponse> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/coverage?mode=${mode}`
    )
  },

  /**
   * Update coverage from direct canvas edits (hybrid mode)
   */
  async updateFromDirectEdit(
    accountId: string,
    projectId: string,
    instanceId: string,
    data: {
      sectionId: string
      items: Array<{ id: string; label: string; description?: string }>
      action: 'add' | 'update' | 'remove'
      mode?: 'guided' | 'organic'
    }
  ): Promise<CanvasDirectEditResponse> {
    const encodedInstanceId = encodeURIComponent(instanceId)
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/canvas/${encodedInstanceId}/coach/coverage/update`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },
}

// ============================================================================
// AI ANALYSIS API
// ============================================================================

export const aiAnalysisApi = {
  /**
   * Analyze a document using the backend workflow system
   * This routes through the document-analysis workflow for prompt management and logging
   */
  async analyzeDocument(
    request: AnalyzeDocumentRequest,
    claudeApiKey?: string
  ): Promise<AnalyzeDocumentResponse> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Pass Claude API key to backend if provided
    if (claudeApiKey) {
      headers['x-claude-api-key'] = claudeApiKey
    }

    return apiFetch<AnalyzeDocumentResponse>('/api/ai/analyze', {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    })
  },

  /**
   * Analyze a single image using visual analysis
   */
  async analyzeImage(
    sourceId: string,
    image: {
      id: string
      data: string
      mimeType?: string
      pageNumber?: number
    },
    existingFacets?: Array<Record<string, unknown>>,
    claudeApiKey?: string
  ): Promise<{
    sourceId: string
    classification?: {
      category: string
      confidence: number
      should_analyze: boolean
    }
    extraction?: Record<string, unknown>
    facets?: Array<Record<string, unknown>>
    analysisTime: number
  }> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (claudeApiKey) {
      headers['x-claude-api-key'] = claudeApiKey
    }

    return apiFetch('/api/ai/analyze-image', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        sourceId,
        image,
        existingFacets,
      }),
    })
  },

  /**
   * Get workflow configuration
   */
  async getWorkflow(key: string): Promise<{
    workflow: {
      id: string
      key: string
      name: string
      description: string
      steps: Array<{
        id: string
        key: string
        name: string
        description: string
        prompts: Array<{
          promptId: string
          promptKey: string
          promptName: string
        }>
      }>
    }
  }> {
    return apiFetch(`/api/ai/workflows/${key}`)
  },
}

// ============================================================================
// SETTINGS API
// ============================================================================

export interface FusekiConfigResponse {
  url: string
  dataset: string
  username: string
  hasPassword: boolean
}

export interface FusekiConfigUpdate {
  url?: string
  dataset?: string
  username?: string
  password?: string
}

export const settingsApi = {
  /**
   * Get current Fuseki configuration
   */
  async getFusekiConfig(): Promise<{ config: FusekiConfigResponse }> {
    return apiFetch('/api/settings/fuseki')
  },

  /**
   * Update Fuseki configuration
   */
  async updateFusekiConfig(config: FusekiConfigUpdate): Promise<{ config: FusekiConfigResponse }> {
    return apiFetch('/api/settings/fuseki', {
      method: 'PUT',
      body: JSON.stringify(config),
    })
  },

  /**
   * Test Fuseki connection with current or provided config
   */
  async testFusekiConnection(
    config?: FusekiConfigUpdate
  ): Promise<{ success: boolean; message: string }> {
    return apiFetch('/api/settings/fuseki/test', {
      method: 'POST',
      body: JSON.stringify(config || {}),
    })
  },
}

// ============================================================================
// AI OPERATION CONFIGURATION API
// ============================================================================

export interface AIOperationConfig {
  operation: string
  name: string
  description: string
  category: string
  provider: string
  model: string
  temperature: number
  maxTokens: number
  requiresVision: boolean
  recommendedQuality: string
  isOverridden: boolean
  overrideId?: string
  updatedBy?: string
  updatedAt?: Date
}

export interface AIOperationConfigUpdate {
  provider?: 'claude' | 'gemini' | 'ollama'
  model?: string
  temperature?: number
  maxTokens?: number
}

export interface AIOperationBulkUpdate {
  operation: string
  provider?: 'claude' | 'gemini' | 'ollama'
  model?: string
  temperature?: number
  maxTokens?: number
}

export const aiConfigApi = {
  /**
   * Get all AI operation configs for an account (with overrides applied)
   */
  async getOperations(accountId: string): Promise<{ operations: AIOperationConfig[] }> {
    return apiFetch(`/api/accounts/${accountId}/ai/operations`)
  },

  /**
   * Get single operation config (with override applied)
   */
  async getOperation(
    accountId: string,
    operation: string
  ): Promise<{ operation: string; config: AIOperationConfigUpdate }> {
    return apiFetch(`/api/accounts/${accountId}/ai/operations/${operation}`)
  },

  /**
   * Update account override for an operation
   */
  async updateOperation(
    accountId: string,
    operation: string,
    config: AIOperationConfigUpdate
  ): Promise<{ override: AIOperationConfig }> {
    console.log('[aiConfigApi] 🔷 updateOperation called:', { accountId, operation, config })
    const result = await apiFetch<{ override: AIOperationConfig }>(`/api/accounts/${accountId}/ai/operations/${operation}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    })
    console.log('[aiConfigApi] 🔷 updateOperation result:', result)
    return result
  },

  /**
   * Delete account override (revert to system default)
   */
  async deleteOverride(accountId: string, operation: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/ai/operations/${operation}`, {
      method: 'DELETE',
    })
  },

  /**
   * Bulk update multiple operations at once
   */
  async bulkUpdate(
    accountId: string,
    updates: AIOperationBulkUpdate[]
  ): Promise<{
    updated: number
    overrides: AIOperationConfig[]
    errors?: Array<{ operation: string; error: string }>
  }> {
    return apiFetch(`/api/accounts/${accountId}/ai/operations/bulk-update`, {
      method: 'POST',
      body: JSON.stringify({ updates }),
    })
  },
}

// ============================================================================
// JOURNEY FRAMEWORK API
// ============================================================================

export interface JourneyFrameworkResponse {
  id: string
  accountId: string
  name: string
  description: string | null
  isDefault: boolean
  isSystem: boolean
  config: JourneyFramework
  createdAt: string
  updatedAt: string
}

export interface ProjectJourneyResponse {
  id: string
  projectId: string
  frameworkId: string | null
  goalId: string | null
  currentPhaseId: string
  phaseProgress: Record<string, number>
  completedActivities: string[]
  completedArtifacts: string[]
  createdAt: string
  updatedAt: string
}

export const journeyApi = {
  /**
   * Get the default framework for the account
   */
  async getFramework(accountId: string): Promise<{ framework: JourneyFrameworkResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/framework`)
  },

  /**
   * Get all frameworks for the account
   */
  async getFrameworks(accountId: string): Promise<{ frameworks: JourneyFrameworkResponse[] }> {
    return apiFetch(`/api/accounts/${accountId}/journey/frameworks`)
  },

  /**
   * Get project journey progress with framework
   */
  async getProjectJourney(
    accountId: string,
    projectId: string
  ): Promise<{ journey: ProjectJourneyResponse; framework: JourneyFrameworkResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey`)
  },

  /**
   * Update project journey progress
   */
  async updateProgress(
    accountId: string,
    projectId: string,
    updates: {
      currentPhaseId?: string
      phaseProgress?: Record<string, number>
      goalId?: string
    }
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/progress`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  },

  /**
   * Mark an activity as completed
   */
  async completeActivity(
    accountId: string,
    projectId: string,
    activityId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/activities/complete`, {
      method: 'POST',
      body: JSON.stringify({ activityId }),
    })
  },

  /**
   * Uncomplete an activity
   */
  async uncompleteActivity(
    accountId: string,
    projectId: string,
    activityId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/activities/uncomplete`, {
      method: 'POST',
      body: JSON.stringify({ activityId }),
    })
  },

  /**
   * Mark an artifact as completed
   */
  async completeArtifact(
    accountId: string,
    projectId: string,
    artifactId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/artifacts/complete`, {
      method: 'POST',
      body: JSON.stringify({ artifactId }),
    })
  },

  /**
   * Select a goal path
   */
  async selectGoal(
    accountId: string,
    projectId: string,
    goalId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/goal`, {
      method: 'POST',
      body: JSON.stringify({ goalId }),
    })
  },

  /**
   * Advance to next phase
   */
  async advancePhase(
    accountId: string,
    projectId: string,
    phaseId: string
  ): Promise<{ journey: ProjectJourneyResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/projects/${projectId}/journey/advance`, {
      method: 'POST',
      body: JSON.stringify({ phaseId }),
    })
  },

  /**
   * Get configurable items (artifacts and entities) for phase configuration
   */
  async getConfigurableItems(
    accountId: string
  ): Promise<ConfigurableItemsResponse> {
    return apiFetch(`/api/accounts/${accountId}/journey/configurable-items`)
  },

  /**
   * Update phase configuration (artifacts and entities)
   */
  async updatePhaseConfig(
    accountId: string,
    frameworkId: string,
    phaseId: string,
    config: UpdatePhaseConfigRequest
  ): Promise<{ framework: JourneyFrameworkResponse }> {
    return apiFetch(`/api/accounts/${accountId}/journey/framework/${frameworkId}/phases/${phaseId}/config`, {
      method: 'PATCH',
      body: JSON.stringify(config),
    })
  },
}

// ============================================================================
// ARTIFACTS API
// ============================================================================

import type {
  ArtifactTypeEnum,
  ArtifactDomain,
  ArtifactStatus,
  Artifact,
  ArtifactTemplate,
  ArtifactTaxonomy,
  ArtifactVersion,
  ArtifactEntityLink,
} from '@ispot/shared'

export interface ArtifactFilters {
  type?: ArtifactTypeEnum
  subType?: string
  domain?: ArtifactDomain
  status?: ArtifactStatus
  journeyPhaseId?: string
  search?: string
}

export interface CreateArtifactRequest {
  type: ArtifactTypeEnum
  subType: string
  domain: ArtifactDomain
  title: string
  description?: string
  templateId?: string
  content?: Record<string, unknown>
  status?: 'draft' | 'active'
  journeyPhaseId?: string
  journeyActivityId?: string
}

export interface UpdateArtifactRequest {
  title?: string
  description?: string
  content?: Record<string, unknown>
  status?: ArtifactStatus
}

export const artifactsApi = {
  // Project-scoped artifact CRUD
  async list(
    accountId: string,
    projectId: string,
    filters?: ArtifactFilters
  ): Promise<{ artifacts: Artifact[]; total: number }> {
    const params = new URLSearchParams()
    if (filters?.type) params.set('type', filters.type)
    if (filters?.subType) params.set('subType', filters.subType)
    if (filters?.domain) params.set('domain', filters.domain)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.journeyPhaseId) params.set('journeyPhaseId', filters.journeyPhaseId)
    if (filters?.search) params.set('search', filters.search)
    const query = params.toString()
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts${query ? `?${query}` : ''}`)
  },

  async get(
    accountId: string,
    projectId: string,
    artifactId: string
  ): Promise<{ artifact: Artifact }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}`)
  },

  async create(
    accountId: string,
    projectId: string,
    data: CreateArtifactRequest
  ): Promise<{ artifact: Artifact }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async update(
    accountId: string,
    projectId: string,
    artifactId: string,
    data: UpdateArtifactRequest
  ): Promise<{ artifact: Artifact }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async archive(
    accountId: string,
    projectId: string,
    artifactId: string
  ): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}`, {
      method: 'DELETE',
    })
  },

  // Version management
  async listVersions(
    accountId: string,
    projectId: string,
    artifactId: string
  ): Promise<{ versions: ArtifactVersion[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/versions`)
  },

  async getVersion(
    accountId: string,
    projectId: string,
    artifactId: string,
    version: number
  ): Promise<{ version: number; content: Record<string, unknown> }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/versions/${version}`)
  },

  async restoreVersion(
    accountId: string,
    projectId: string,
    artifactId: string,
    version: number
  ): Promise<{ artifact: Artifact; restored: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/versions/${version}/restore`, {
      method: 'POST',
    })
  },

  // Entity links
  async getEntityLinks(
    accountId: string,
    projectId: string,
    artifactId: string,
    sectionId?: string
  ): Promise<{ links: ArtifactEntityLink[] }> {
    const query = sectionId ? `?sectionId=${sectionId}` : ''
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities${query}`)
  },

  async linkEntity(
    accountId: string,
    projectId: string,
    artifactId: string,
    data: {
      entityUri: string
      entityType: string
      entityLabel: string
      linkType?: 'reference' | 'derived' | 'supports'
      sectionId?: string
      contextNote?: string
      sortOrder?: number
    }
  ): Promise<{ link: ArtifactEntityLink }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async unlinkEntity(
    accountId: string,
    projectId: string,
    artifactId: string,
    linkId: string
  ): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities/${linkId}`, {
      method: 'DELETE',
    })
  },

  // Create entity in graph AND link to canvas (for coach suggestions)
  async createAndLinkEntity(
    accountId: string,
    projectId: string,
    artifactId: string,
    data: {
      sectionId: string
      label: string
      entityType: string
      description?: string
      note?: string
      sortOrder?: number
    }
  ): Promise<{ entityUri: string; entityType: string; link: ArtifactEntityLink }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/entities/create`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Migration
  async migrateCanvas(
    accountId: string,
    projectId: string,
    data: {
      templateKey: string
      title: string
      sections: Record<string, {
        notes?: string
        items?: Array<{
          id: string
          entityUri: string
          label: string
          entityType?: string
          note?: string
          sortOrder: number
          addedAt: string
        }>
      }>
    }
  ): Promise<{ success: boolean; artifactId?: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/migrate/canvas`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async migrateDoc(
    accountId: string,
    projectId: string,
    data: {
      title: string
      tiptapContent: Record<string, unknown> | null
      plainText: string
      subType?: string
      domain?: ArtifactDomain
    }
  ): Promise<{ success: boolean; artifactId?: string }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/migrate/doc`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getMigrationStatus(
    accountId: string,
    projectId: string
  ): Promise<{ hasArtifacts: boolean; artifactCounts: Record<ArtifactTypeEnum, number> }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/migration-status`)
  },

  // Journey integration
  async linkToJourney(
    accountId: string,
    projectId: string,
    artifactId: string,
    journeyPhaseId: string | null,
    journeyActivityId: string | null
  ): Promise<{ artifact: Artifact }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/${artifactId}/journey`, {
      method: 'PATCH',
      body: JSON.stringify({ journeyPhaseId, journeyActivityId }),
    })
  },

  async getForPhase(
    accountId: string,
    projectId: string,
    phaseId: string
  ): Promise<{ artifacts: Artifact[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/journey/phase/${phaseId}`)
  },

  async getForActivity(
    accountId: string,
    projectId: string,
    activityId: string
  ): Promise<{ artifacts: Artifact[] }> {
    return apiFetch(`/api/accounts/${accountId}/projects/${projectId}/artifacts/journey/activity/${activityId}`)
  },
}

// ============================================================================
// ARTIFACT TEMPLATES API (Account-scoped)
// ============================================================================

export const artifactTemplatesApi = {
  async list(accountId: string): Promise<{ templates: ArtifactTemplate[] }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates`)
  },

  async getTaxonomy(accountId: string): Promise<{ taxonomy: ArtifactTaxonomy }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/taxonomy`)
  },

  async get(accountId: string, templateId: string): Promise<{ template: ArtifactTemplate }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/${templateId}`)
  },

  async create(
    accountId: string,
    data: {
      key: string
      name: string
      description?: string
      type: ArtifactTypeEnum
      subType: string
      domain: ArtifactDomain
      structure: Record<string, unknown>
      defaultContent?: Record<string, unknown>
      icon?: string
      color?: string
    }
  ): Promise<{ template: ArtifactTemplate }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async update(
    accountId: string,
    templateId: string,
    data: {
      name?: string
      description?: string
      structure?: Record<string, unknown>
      defaultContent?: Record<string, unknown>
      icon?: string
      color?: string
      isActive?: boolean
      sortOrder?: number
    }
  ): Promise<{ template: ArtifactTemplate }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async delete(accountId: string, templateId: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/${templateId}`, {
      method: 'DELETE',
    })
  },

  async initialize(accountId: string): Promise<{ templates: ArtifactTemplate[]; initialized: number }> {
    return apiFetch(`/api/accounts/${accountId}/artifacts/templates/initialize`, {
      method: 'POST',
    })
  },
}

// ============================================================================
// DOC TEMPLATES API
// ============================================================================

/**
 * Doc Templates API - Template configurator for Smart Docs
 * Manage document templates with section configuration and entity shortcuts
 */
export const docTemplatesApi = {
  /**
   * List all doc templates for an account
   */
  async list(accountId: string): Promise<DocTemplatesListResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates`)
  },

  /**
   * Get a specific doc template with full configuration
   */
  async get(accountId: string, templateId: string): Promise<DocTemplateGetResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/${templateId}`)
  },

  /**
   * Create a new doc template
   */
  async create(
    accountId: string,
    data: CreateDocTemplateRequest
  ): Promise<DocTemplateGetResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Update template configuration (sections + entity shortcuts)
   */
  async updateConfig(
    accountId: string,
    templateId: string,
    config: UpdateDocTemplateConfigRequest
  ): Promise<DocTemplateGetResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/${templateId}/config`, {
      method: 'PATCH',
      body: JSON.stringify(config),
    })
  },

  /**
   * Delete a doc template
   */
  async delete(accountId: string, templateId: string): Promise<{ success: boolean }> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/${templateId}`, {
      method: 'DELETE',
    })
  },

  /**
   * Get configurable items for drag & drop (section templates + entities)
   */
  async getConfigurableItems(accountId: string): Promise<DocConfigurableItemsResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/configurable-items`)
  },

  /**
   * List section templates
   */
  async listSectionTemplates(accountId: string): Promise<SectionTemplatesListResponse> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/section-templates`)
  },

  /**
   * Create a new section template
   */
  async createSectionTemplate(
    accountId: string,
    data: CreateSectionTemplateRequest
  ): Promise<{ section: SectionTemplate }> {
    return apiFetch(`/api/accounts/${accountId}/docs/templates/section-templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  /**
   * Create a document from a template
   */
  async createDocFromTemplate(
    accountId: string,
    projectId: string,
    templateId: string,
    data: { title: string }
  ): Promise<{ doc: unknown }> {
    return apiFetch(
      `/api/accounts/${accountId}/projects/${projectId}/docs/from-template/${templateId}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    )
  },
}
